import { range, uniq, uniqBy, isEqual } from 'lodash';
import { isTruthy } from '@ardoq/common-helpers';
import { computeLegendComponentTypes } from '../../viewModel$/legendComponentTypes';
import type { APIComponentAttributes, LinkedComponent } from '@ardoq/api-types';
import {
  APIFieldAttributes,
  APIFieldType,
  APIOrganizationUser,
  ArdoqId,
  isFormattingFilter,
  ViewIds,
} from '@ardoq/api-types';
import {
  componentUtils,
  enhancedScopeDataOperations,
  fieldUtils,
  type FilterColorData,
  fieldValueFormatters,
  referenceUtils,
  scopeDataOperations,
} from '@ardoq/scope-data';
import { deriveFilterColorData } from '@ardoq/filter-interface';
import { componentOperations, usersOperations } from '@ardoq/core';
import { type PermissionContext } from '@ardoq/access-control';
import {
  type FieldWithPermissions,
  getFieldsForViewpoint,
} from 'streams/fields/getFieldsForViewpoint';
import { getGlobalReferenceType } from '@ardoq/renderers';
import { isUndefinedOrNull } from 'utils/collectionUtil';
import { componentMatrixToggleCollapseGroup } from '../../actions';
import {
  COMPONENT_HIERARCHY_PADDING,
  INCOMING_SUFFIX,
  LEVEL_HEADER_CELL_HORIZONTAL_PADDING,
  NONE_HEADER_LABEL,
  OUTGOING_SUFFIX,
} from '../../consts';
import { COMPONENT_MATRIX_COMPONENTS_LIMIT } from 'views/environmentals/viewDataLimits';
import {
  CARD_GROUP_HORIZONTAL_MARGIN,
  DEFAULT_REPRESENTATION_DATA,
  LEVEL_HEADER_CARD_MAX_WIDTH,
  LEVEL_HEADER_CARD_MIN_WIDTH,
  LEVEL_HEADERS_CARD_GAP,
  LEVEL_HEADERS_CONTAINER_HORIZONTAL_PADDING,
  MAX_COLUMN_HEADER_WIDTH,
  MAX_ROW_HEADER_WIDTH,
  ROW_GROUP_VERTICAL_MARGIN,
} from '../consts';
import {
  ComponentMatrixDimension,
  ComponentMatrixGroup,
  ComponentMatrixGroupOwnerType,
  ComponentMatrixViewModel,
} from '../../types';
import {
  Axis,
  AxisSortOrder,
  ComponentMatrix2Group,
  ComponentMatrix2Item,
  ComponentMatrix2ViewSettings,
  CountSortContext,
  GroupColorInfo,
  LevelHeaderViewModel,
  NestedCountMaps,
  PersistedExpandedState,
} from '../types';
import { dateRangeOperations } from '@ardoq/date-range';
import { hasChildren } from '../../util';
import { sanitizeSelectedComponentTypes } from '../utils';
import { addAddressToGroup } from '../../viewModel$/addressUtil';
import {
  extractReferenceTypeId,
  extractWorkspaceId,
  formatReferenceType,
} from '../../viewModel$/referenceAddressUtil';
import {
  ComponentGraphData,
  expandAddressWithChildren,
  getComponentHierarchyAddress,
  getLinkedComponentsAddresses,
  getParentAddresses,
} from './referenceAddressUtil';
import { type ExtractPayload } from '@ardoq/rxbeach';
import { loadedGraphOperations } from 'traversals/loadedGraphOperations';
import { measureLevelLabel } from '../../viewModel$/measureLabels';
import { getRowHeaderModels } from './getRowHeaderModels';
import {
  toComponentMatrix2AxisSources,
  toComponentMatrix2SettingsBounds,
  toComponentMatrix2StoredSettableSettings,
} from '../settingsDependencies';
import {
  getGraphInterfaceForEnhancedScopeData,
  getLabelsInterfaceForEnhancedScopeData,
  getOtherLabelsForComponent,
  getOtherLabelsForReference,
  getUrlFieldValuesByComponentId,
  newConditionalFormattingMultiLabelStyle,
  OtherLabel,
  UrlFieldValue,
} from '@ardoq/graph';
import type {
  LabelsInterface,
  LoadedGraphWithViewpointMode,
} from '@ardoq/graph';
import { getLuminance, mix } from 'polished';
import { colors } from '@ardoq/design-tokens';
import {
  ComponentMatrix2AxisSources,
  ComponentMatrix2BuiltState,
  ComponentMatrix2HeaderCellModel,
  ComponentMatrix2SourceData,
  ComponentMatrix2ViewSettingsState,
  ColumnResizeHandleModel,
  RowElementModel,
  ComponentTypeStyle,
  ViewpointModeComponentMatrixMeasurements,
  ViewpointModeComponentMatrixViewModel,
  ViewpointModeComponentMatrixViewStreamState,
} from '../types';
import {
  buildColumnsByDepthWithGridInfo,
  clampRowHeadersAreaWidth,
  augmentBodyCellItemsWithOtherLabelWrappedLines,
  columnHeadersAreaGridTemplateRows,
  computeColumnHeaderOtherLabelWrappedLines,
  computeRowHeaderOtherLabelWrappedLines,
  EMPTY_COLUMN_WIDTH,
  computeRawRowHeaderWidth,
  buildVisibleLeafCounts,
  getColumnHeaderLevelHeights,
  getColumnHeaderLevelStripeHeights,
  getComponentRowHeaderHeight,
  getComponentRowHeaderLineCount,
  getHeaderColumnWidths,
  getMaxWrappedLinesForColumn,
  getPaddingTracks,
  levelHasRenderableContent,
  getListValueRowHeaderLineCount,
  measureCells,
  measureRowHeaders,
  measureRowLevelHeaders,
} from './measureCells';
import * as profiling from '@ardoq/profiling';
import {
  type TraversedReferenceTypeInfo,
  getReferenceTypeInfo,
} from '@ardoq/settings-bar';
import { paddedAddressPermutations } from '../../viewModel$/paddedAddressPermutations';
import { ItemsType, nodeLimitError } from '@ardoq/error-info-box';
import { DEFAULT_ARDOQ_I18NLOCALE, t, type I18nLocale } from '@ardoq/i18n';
import {
  getErrorInfoBoxLabels,
  getNodeLimitErrorLabels,
} from 'locales/getErrorInfoBoxLabels';
import {
  ARDOQ_DEFAULT_LOCALE,
  formatNumber,
  type Locale,
  localeCompare,
  localeCompareNumeric,
  localeCompareNumericLowercase,
} from '@ardoq/locale';
import {
  compareComponentsById,
  getComponentComparator,
  getDefaultSort,
  SortAttribute,
  type ComponentByIdSortDependencies,
} from '@ardoq/common-helpers';
import {
  ComponentLabelSource,
  COMPONENT_OR_REFERENCE_DESCRIPTION_FIELD_LABEL,
  NumericSortOrder,
  type ContextSort,
  type EnhancedScopeData,
  type FormattingAndLabelRules,
  type GraphModelShape,
  type RepresentationData,
} from '@ardoq/data-model';
import { getDefaultViewSettings } from 'views/environmentals/viewSettings/getDefaultViewSettings';
import { LUMINANCE_THRESHOLD } from 'tabview/consts';

/**
 * Number of visible leaf tracks before groupValue within groups.
 * (Analogous to getGroupIndex in shared getRowModels but expansion-aware.)
 */
export const getVisibleGroupIndex = (
  groups: Map<string | null, ComponentMatrixGroup>,
  groupValue: string | null,
  leafCounts: Map<ComponentMatrixGroup, number>
): number => {
  let count = 0;
  for (const [key, group] of groups) {
    if (key === groupValue) break;
    count += leafCounts.get(group) ?? 1;
  }
  return count;
};

/**
 * Maps an item address to its visible-leaf track index.
 * Stops traversing when it reaches a collapsed group (the collapsed group IS
 * the track, so all its descendants share the same index).
 */
export const getVisibleItemIndex = (
  address: (string | null)[],
  groups: Map<string | null, ComponentMatrixGroup>,
  leafCounts: Map<ComponentMatrixGroup, number>
): number => {
  if (!address.length) return 0;
  const [head, ...tail] = address;
  const groupValue = head ?? null;
  let index = getVisibleGroupIndex(groups, groupValue, leafCounts);
  let currentGroup = groups.get(groupValue);
  let remaining = tail;
  while (
    remaining.length &&
    currentGroup?.isExpanded &&
    currentGroup.children.size
  ) {
    const [next, ...rest] = remaining;
    const nextValue = next ?? null;
    index += getVisibleGroupIndex(currentGroup.children, nextValue, leafCounts);
    currentGroup = currentGroup.children.get(nextValue);
    remaining = rest;
  }
  return index;
};

export const buildCM2ItemMap = (
  viewModel: ViewpointModeComponentMatrixViewModel,
  leafCounts: Map<ComponentMatrixGroup, number>
): Map<string, ComponentMatrix2Item[]> => {
  const result = new Map<string, ComponentMatrix2Item[]>();
  for (const item of viewModel.items) {
    const colIdx = getVisibleItemIndex(
      item.address.columnValues,
      viewModel.columns,
      leafCounts
    );
    const rowIdx = getVisibleItemIndex(
      item.address.rowValues,
      viewModel.rows,
      leafCounts
    );
    const key = `${colIdx},${rowIdx}`;
    (result.get(key) ?? result.set(key, []).get(key)!).push(item);
  }
  return result;
};

/**
 * Returns the set of 0-based row indices that are the LAST leaf row of each
 * non-final root row group. These rows get an extra ROW_GROUP_VERTICAL_MARGIN
 * px added to their grid track so the row-header area can show a visual gap
 * between root groups without using CSS row-gap (which would also gap the body).
 */
export const computeBoundaryRowIndices = (
  rows: Map<string | null, ComponentMatrixGroup>,
  leafCounts: Map<ComponentMatrixGroup, number>
): Set<number> => {
  const rootGroups = [...rows.values()];
  const boundaryRowIndices = new Set<number>();
  let cumulativeLeaves = 0;
  for (let i = 0; i < rootGroups.length - 1; i++) {
    cumulativeLeaves += leafCounts.get(rootGroups[i]) ?? 1;
    boundaryRowIndices.add(cumulativeLeaves - 1);
  }
  return boundaryRowIndices;
};

export type HalfAddress = (string | null)[];

/**
 * Combine the reference-side and field-side halves of an address into the
 * final group key paths. `fieldAddresses` is a list because a focal with a
 * multi-list field value fans out into multiple field halves (ARD-31884);
 * the result is the cartesian product `referenceAddresses × fieldAddresses`.
 * When there are no reference addresses, the field halves stand alone.
 *
 * `fieldAddresses` must be non-empty — callers represent "no field values"
 * with `[[]]` (a single empty half), not `[]`. Passing `[]` while
 * `referenceAddresses` is non-empty would silently yield `[]` (flatMap
 * over an empty array), dropping the reference halves entirely.
 */
export const combineHalfAddresses = (
  fieldAddresses: HalfAddress[],
  referenceAddresses: HalfAddress[]
): HalfAddress[] =>
  referenceAddresses.length
    ? referenceAddresses.flatMap(referenceAddress =>
        fieldAddresses.map(fieldAddress => [
          ...referenceAddress,
          ...fieldAddress,
        ])
      )
    : fieldAddresses;

export const listFieldLabel = (
  fieldName: string,
  listFields: APIFieldAttributes[]
): string => {
  const field =
    fieldName &&
    listFields &&
    listFields.find(field => field.name === fieldName);
  if (!field) {
    return 'Unknown';
  }
  return field.label;
};

export const groupLevelLabels = (
  referenceTypeName: string,
  maxDepth: number,
  isOutgoing?: boolean
): string[] => {
  if (!referenceTypeName) {
    return [];
  }

  const suffix =
    isOutgoing === undefined
      ? ''
      : isOutgoing
        ? OUTGOING_SUFFIX
        : INCOMING_SUFFIX;
  const levelLabel = `${referenceTypeName}${suffix}`;
  return maxDepth <= 1
    ? [levelLabel]
    : [levelLabel].concat(
        Array(maxDepth - 1).fill(COMPONENT_HIERARCHY_PADDING)
      );
};

export const isComponentHierarchyPadding = (
  group: ComponentMatrixGroup
): boolean =>
  group.address[group.address.length - 1] === COMPONENT_HIERARCHY_PADDING;

export const findDuplicates = <T>(
  collection: T[],
  isDuplicate: (itemA: T, itemB: T) => boolean
): T[] =>
  collection.filter(
    (item, index, array) =>
      array.findIndex(itemB => isDuplicate(item, itemB)) !== index
  );

export const cloneMap = <K, V>(
  target: Map<K, V>,
  cloneValue: (value: V) => V = value => Object.assign({}, value)
): Map<K, V> =>
  new Map(
    [...target.entries()].map(([key, value]) => [key, cloneValue(value)])
  );

export const cloneGroup = (
  group: ComponentMatrixGroup
): ComponentMatrixGroup => ({
  ...group,
  children: cloneMap(group.children, cloneGroup),
});

export const toLevelHeaderViewModel = (
  labels: string[]
): LevelHeaderViewModel | undefined => {
  const rawLabel = labels.find(
    label => label && label !== COMPONENT_HIERARCHY_PADDING
  );
  if (!rawLabel) return undefined;
  const isOutgoing = rawLabel.endsWith(OUTGOING_SUFFIX);
  const isIncoming = rawLabel.endsWith(INCOMING_SUFFIX);
  return {
    fieldName:
      isOutgoing || isIncoming
        ? rawLabel.slice(0, -OUTGOING_SUFFIX.length)
        : rawLabel,
    referenceDirection: isOutgoing
      ? 'outgoing'
      : isIncoming
        ? 'incoming'
        : undefined,
  };
};

const HOVER_OVERLAY_COLOR = colors.black950;
// Light fills get a 10% overlay; dark fills get 30%
const HOVER_OVERLAY_OPACITY_LIGHT = 0.1;
const HOVER_OVERLAY_OPACITY_DARK = 0.3;

export const getAxisHeaderDisplayLabel = (
  displayTextOverride: string | null | undefined,
  foundReferenceType: TraversedReferenceTypeInfo | null | undefined,
  selectedFieldNames: string[],
  listFields: APIFieldAttributes[]
): string =>
  displayTextOverride ??
  (foundReferenceType
    ? foundReferenceType.displayName
    : selectedFieldNames.length > 0
      ? // Axis header is a single display string. In the field fallback case,
        // use the first selected field as the representative label; multi-field
        // level labels are handled separately when building column/row level labels.
        listFieldLabel(selectedFieldNames[0], listFields)
      : '');

const getReferencedComponentRepresentationData = (
  scopeData: EnhancedScopeData,
  ownerType: ComponentMatrixGroupOwnerType,
  componentId: string | undefined
): RepresentationData | undefined => {
  if (
    ownerType !== ComponentMatrixGroupOwnerType.REFERENCED_COMPONENT ||
    !componentId
  ) {
    return undefined;
  }
  const component = enhancedScopeDataOperations.getComponent(
    scopeData,
    componentId
  );
  if (!component) return undefined;
  // `componentOperations.getRepresentationData` short-circuits on
  // `component.image` / `component.icon` before consulting the type, so
  // skip the type lookup in those cases. The lookup `invariant`s on
  // missing model types — story fixtures populate components with just
  // an `icon` field, so the lookup would crash there.
  const type =
    component.image || component.icon
      ? null
      : enhancedScopeDataOperations.getComponentTypeByComponentId(
          scopeData,
          componentId
        );
  return componentOperations.getRepresentationData(component, type);
};

const serializeGroupAddress = (address: (string | null)[]): string =>
  address.map(v => (v === null ? '\0' : v)).join('|');

export const getCountMapLookupKey = (
  axis: Axis,
  depth: number,
  parentAddress: (string | null)[]
): string => `${axis}:${depth}:${serializeGroupAddress(parentAddress)}`;

export const buildNestedCountMaps = (
  allItems: ComponentMatrix2Item[],
  axis: Axis
): NestedCountMaps => {
  const result: NestedCountMaps = new Map();

  for (const item of allItems) {
    const values =
      axis === Axis.COLUMN ? item.address.columnValues : item.address.rowValues;

    for (let depth = 0; depth < values.length; depth++) {
      const parentAddress = values.slice(0, depth);
      const keyAtDepth = values[depth] ?? null;
      const lookupKey = getCountMapLookupKey(axis, depth, parentAddress);

      let countMap = result.get(lookupKey);
      if (!countMap) {
        countMap = new Map<string | null, number>();
        result.set(lookupKey, countMap);
      }

      countMap.set(keyAtDepth, (countMap.get(keyAtDepth) ?? 0) + 1);
    }
  }

  return result;
};

type BodyCellItemArgs = {
  componentId: ArdoqId;
  referenceIds: ArdoqId[];
  urlFieldValues: UrlFieldValue[] | null;
  isContext: boolean;
  labelsInterface: LabelsInterface;
  i18nLocale: I18nLocale;
  scopeData: EnhancedScopeData;
  filterColorData: FilterColorData;
};

const toBodyCellItem = ({
  componentId,
  referenceIds,
  urlFieldValues,
  isContext,
  labelsInterface,
  i18nLocale,
  scopeData,
  filterColorData,
}: BodyCellItemArgs) => {
  const fillColor =
    componentUtils.getComponentDisplayColorAsSVGAttributes(
      scopeData,
      filterColorData,
      componentId,
      { useAsBackgroundStyle: false }
    ).fill ?? '';
  const isDark = fillColor
    ? getLuminance(fillColor) < LUMINANCE_THRESHOLD
    : false;
  const hoverFillColor = fillColor
    ? mix(
        isDark ? HOVER_OVERLAY_OPACITY_DARK : HOVER_OVERLAY_OPACITY_LIGHT,
        HOVER_OVERLAY_COLOR,
        fillColor
      )
    : '';
  const representationData =
    getReferencedComponentRepresentationData(
      scopeData,
      ComponentMatrixGroupOwnerType.REFERENCED_COMPONENT,
      componentId
    ) ?? DEFAULT_REPRESENTATION_DATA;
  return {
    componentId,
    componentLabel: labelsInterface.getComponentDisplayName(componentId) ?? '',
    fillColor,
    hoverFillColor,
    isDark,
    isContext,
    representationData,
    urlFieldValues,
    otherLabels: [
      ...(getOtherLabelsForComponent(
        labelsInterface,
        componentId,
        i18nLocale
      ) ?? []),
      ...referenceIds.flatMap(
        referenceId =>
          getOtherLabelsForReference(
            labelsInterface,
            referenceId,
            i18nLocale
          ) ?? []
      ),
    ],
  };
};

const toViewpointRowModels = (
  viewModel: ViewpointModeComponentMatrixViewModel,
  i18nLocale: I18nLocale,
  orgUsersById: Record<ArdoqId, APIOrganizationUser>,
  contextComponentId: ArdoqId | null,
  scopeData: EnhancedScopeData,
  filterColorData: FilterColorData,
  colorByComponent: boolean,
  formattingAndLabelRules: FormattingAndLabelRules['rules']
): RowElementModel[] => {
  const leafCounts = buildVisibleLeafCounts([
    ...viewModel.columns.values(),
    ...viewModel.rows.values(),
  ]);
  const itemMap = buildCM2ItemMap(viewModel, leafCounts);
  // Total tracks = visible leaf count from root columns.
  // columnsByDepth[last] only holds the deepest groups — root-level groups with
  // no children (e.g. "none") don't appear there, so their tracks would be absent.
  const totalColumnTracks = [...viewModel.columns.values()].reduce(
    (acc, group) => acc + (leafCounts.get(group) ?? 1),
    0
  );

  const allComponentIds = uniq(viewModel.items.map(item => item.componentId));
  const labelsInterface = getLabelsInterfaceForEnhancedScopeData({
    enhancedScopeData: scopeData,
    formattingFilters: formattingAndLabelRules,
    i18nLocale,
    formatUserFieldFn: val =>
      usersOperations.formatUserField(orgUsersById, val),
  });
  /** CM2 has no `GroupingRule` concept, hence the empty array.
   * Don't reuse `viewInterface` for hierarchy accessors (e.g. `getComponentParentId`) — they'd resolve as ungrouped. */
  const viewInterface = {
    ...getGraphInterfaceForEnhancedScopeData({
      enhancedScopeData: scopeData,
      conditionalFormatting: formattingAndLabelRules,
      groupingRules: [],
      i18nLocale,
    }),
    ...labelsInterface,
  };
  const urlFieldValuesByComponentId = getUrlFieldValuesByComponentId(
    viewInterface,
    allComponentIds
  );

  const boundaryRowIndices = computeBoundaryRowIndices(
    viewModel.rows,
    leafCounts
  );

  // Compute the rendered row-headers area width up-front so we can derive
  // wrap-aware stripe heights for each headerCell that match the actual
  // rendered card width — same width that getComponentRowHeaderHeight will
  // use later for row-track sizing in measureRowHeaders. Without this, the
  // expanded-group stripe stays at ROW_HEADER_STRIPE_HEIGHT (42px) even
  // when the label wraps to multiple lines.
  const initialRows = getRowHeaderModels(viewModel.rows, leafCounts);
  const initialRowHeadersAreaWidth = clampRowHeadersAreaWidth(
    initialRows.map(computeRawRowHeaderWidth)
  );

  return initialRows.map((row, rowIndex) => ({
    headerCells: row.headerCells.map(
      (cell): ComponentMatrix2HeaderCellModel => {
        // stripeHeight is only consumed by the renderer for expanded
        // non-leaf cells at level > 0 (see componentMatrixRowHeaderCell).
        // Skip the wrapString measurement work for everything else.
        const level = cell.indexAddress.length - 1;
        const needsStripeHeight =
          cell.group.ownerType ===
            ComponentMatrixGroupOwnerType.REFERENCED_COMPONENT &&
          level > 0 &&
          cell.group.isExpanded &&
          hasChildren(cell.group);
        // Compute the wrapped-line count for component row headers so the
        // renderer can flip the icon/badge/expander cross-axis alignment
        // between `center` (single-line) and `flex-start` (multi-line) per
        // the Figma spec — node 9260:12713, ARD-31373. List-value row
        // headers get `labelLineCount` from a different pass below in
        // `augmentListValueCellLabelLineCount`.
        const isComponentReference =
          cell.group.ownerType ===
          ComponentMatrixGroupOwnerType.REFERENCED_COMPONENT;
        const componentLabelLineCount = isComponentReference
          ? getComponentRowHeaderLineCount(
              cell.group,
              level,
              initialRowHeadersAreaWidth
            )
          : undefined;
        const cardWidth =
          initialRowHeadersAreaWidth - 2 * CARD_GROUP_HORIZONTAL_MARGIN * level;
        const otherLabelWrappedLines =
          isComponentReference && cell.group.otherLabels.length > 0
            ? computeRowHeaderOtherLabelWrappedLines(
                cell.group.otherLabels,
                cardWidth
              )
            : undefined;
        const representationData = getReferencedComponentRepresentationData(
          scopeData,
          cell.group.ownerType,
          cell.value ?? undefined
        );
        return {
          ...cell,
          ...(colorByComponent
            ? getGroupColors(
                cell.value,
                cell.group.ownerType,
                scopeData,
                filterColorData
              )
            : {
                fillColor: colors.grey400,
                borderColor: colors.grey200,
                isDark: false,
              }),
          ...(needsStripeHeight
            ? {
                stripeHeight: getComponentRowHeaderHeight(
                  cell.group,
                  level,
                  initialRowHeadersAreaWidth,
                  componentLabelLineCount
                ),
              }
            : {}),
          ...(componentLabelLineCount !== undefined
            ? { labelLineCount: componentLabelLineCount }
            : {}),
          ...(otherLabelWrappedLines !== undefined
            ? { otherLabelWrappedLines }
            : {}),
          ...(representationData !== undefined ? { representationData } : {}),
        };
      }
    ),
    bodyCells: range(totalColumnTracks).map(columnIndex => {
      const rawItems = itemMap.get(`${columnIndex},${rowIndex}`) ?? [];
      const uniqueItems = uniqBy(rawItems, 'componentId');
      return {
        items: uniqueItems.map(item =>
          toBodyCellItem({
            componentId: item.componentId,
            referenceIds: item.referenceIds ?? [],
            urlFieldValues:
              urlFieldValuesByComponentId.get(item.componentId) ?? null,
            isContext: contextComponentId === item.componentId,
            labelsInterface,
            i18nLocale,
            scopeData,
            filterColorData,
          })
        ),
        columnIndex,
        columnSpan: 1,
        rowSpan: 1,
      };
    }),
    rowBottomGap: boundaryRowIndices.has(rowIndex)
      ? ROW_GROUP_VERTICAL_MARGIN
      : 0,
  }));
};

type Address = {
  columnValues: HalfAddress;
  rowValues: HalfAddress;
};

/**
 * For each address, generate every suffix starting from each non-null,
 * non-padding position. This ensures a component appears as a body cell
 * wherever its referenced component is visible in the column/row structure —
 * whether as a leaf (full ancestor chain shown) or as a group ancestor
 * (shorter chain, when another component references a descendant and the
 * target appears as a parent group at a higher depth level).
 */
const addressSuffixes = (addrs: HalfAddress[]): HalfAddress[] =>
  addrs.flatMap(addr => {
    const suffixes: HalfAddress[] = [addr];
    for (let i = 1; i < addr.length; i++) {
      const head = addr[i];
      if (head === null || head === COMPONENT_HIERARCHY_PADDING) break;
      suffixes.push(addr.slice(i));
    }
    return suffixes;
  });

const initialStateMeasurements = () => ({
  mainContainerLeftColumnWidth: 0,
  columnHeaderWidths: [],
  columnHeaderAndColumnWidths: [],
  rowHeights: [],
  columnHeadersAreaHeight: 0,
  columnHeaderLevelStripeHeights: [],
  columnHeadersAreaGridTemplateRows: '',
  leafColumnKeys: [],
  leafColumnMinWidths: [],
  leafRowKeys: [],
  leafRowMinHeights: [],
  rowHeadersAreaMinWidth: 0,
});
const initialViewSettings: ComponentMatrix2ViewSettings =
  getDefaultViewSettings(ViewIds.COMPONENT_MATRIX_2, true);

const initialViewSettingsState: ComponentMatrix2ViewSettingsState = {
  listFields: [],
  additionalHeaderFieldsX: [],
  additionalHeaderFieldsY: [],
  traversedOutgoingReferenceTypes: [],
  traversedIncomingReferenceTypes: [],
  quickPerspectiveReferenceTypeFilters: null,
  availableComponentTypes: [],
  componentTypeStylesByName: {},
};

const initialAxisSources = toComponentMatrix2AxisSources({
  viewSettings: initialViewSettings,
  listFields: initialViewSettingsState.listFields,
  axisReferences: [],
  additionalHeaderFieldsX: initialViewSettingsState.additionalHeaderFieldsX,
  additionalHeaderFieldsY: initialViewSettingsState.additionalHeaderFieldsY,
});

export const initialState: ViewpointModeComponentMatrixViewStreamState = {
  viewSettings: initialViewSettings,
  viewSettingsState: initialViewSettingsState,
  settingsBounds: toComponentMatrix2SettingsBounds(initialViewSettingsState),
  settableViewSettings: toComponentMatrix2StoredSettableSettings({
    viewSettings: initialViewSettings,
    selectedComponentTypes: [],
    axisSources: initialAxisSources,
  }),
  viewModel: {
    columns: new Map(),
    rows: new Map(),
    items: [],
    columnsByDepth: [],
    columnsByDepthWithGridInfo: [],
    rowsByDepth: [],
    columnLevelLabels: [],
    rowLevelLabels: [],
    measurements: initialStateMeasurements(),
    rowModels: [],
    hoveredComponentId: null,
    focusedComponentId: null,
    userAdjustedColumnWidths: {},
    userAdjustedRowHeights: {},
    resizeHandles: [],
    legendComponentTypes: [],
  },
  hasReferences: false,
  hasFilteredNullItems: false,
  errors: [],
  contextComponentId: null,
  i18nLocale: DEFAULT_ARDOQ_I18NLOCALE,
  orgUsersById: {},
  errorInfoBoxLabels: getErrorInfoBoxLabels(DEFAULT_ARDOQ_I18NLOCALE),
  sort: getDefaultSort(),
  userLocale: ARDOQ_DEFAULT_LOCALE,
  scopeData: scopeDataOperations.enhance(scopeDataOperations.getEmpty()),
  loadedGraph: loadedGraphOperations.getEmpty(),
  // Placeholder; replaced on the first `resetReducer` tick with the
  // actual context from `currentUserPermissionContext$`.
  permissionContext: {
    user: null,
    permissions: {},
    confidentialFieldPermissions: null,
  },
  filterColorData: { entities: {} },
  bypassLimit: false,
  formattingAndLabelRules: [],
};

const EMPTY_GROUP_MAP = new Map<string | null, ComponentMatrixGroup>([
  [
    null,
    {
      address: [null],
      ownerType: ComponentMatrixGroupOwnerType.EMPTY,
      label: '',
      isExpanded: false,
      isHidden: false,
      children: new Map(),
      otherLabels: [],
    },
  ],
]);

const collectExpandedAddresses = (
  groups: Map<string | null, ComponentMatrixGroup>
): (string | null)[][] => {
  const result: (string | null)[][] = [];
  groups.forEach(group => {
    if (isComponentHierarchyPadding(group)) return;
    if (group.isExpanded) {
      result.push(group.address);
      result.push(...collectExpandedAddresses(group.children));
    }
  });
  return result;
};

const expandGroupAtAddress = (
  groups: Map<string | null, ComponentMatrixGroup>,
  [head, ...tail]: (string | null)[]
): void => {
  const group = groups.get(head);
  if (!group) return;
  if (tail.length === 0) {
    group.isExpanded = true;
    group.children.forEach(child => setHidden(child, false));
    expandComponentHierarchyPadding(group);
  } else {
    expandGroupAtAddress(group.children, tail);
  }
};

const applyPersistedExpandedState = (
  viewModel: ComponentMatrixViewModel,
  { expandedRowAddresses, expandedColumnAddresses }: PersistedExpandedState
): void => {
  const sortByDepth = (a: (string | null)[], b: (string | null)[]) =>
    a.length - b.length;
  [...expandedRowAddresses]
    .sort(sortByDepth)
    .forEach(address => expandGroupAtAddress(viewModel.rows, address));
  [...expandedColumnAddresses]
    .sort(sortByDepth)
    .forEach(address => expandGroupAtAddress(viewModel.columns, address));
};

/**
 * Bundle of pre-resolved per-id data and resolver functions consumed by the
 * pure CM2 view-model seam (`getViewModel` and its transitive helpers). All
 * model-interface reads must live in the wrapper that builds these — the
 * seam itself stays deterministic w.r.t. its inputs (ARD-31996).
 */
export type DataResolvers = ComponentGraphData & {
  /** Pre-filtered linked-component fan-outs for every focal in startSet, by
   * the same `isIncludedInContextByFilter` reference predicate the view used
   * to apply inline. Keyed only on startSet ids — the seam never calls these
   * on anyone else. */
  sourcesById: Record<ArdoqId, LinkedComponent[]>;
  targetsById: Record<ArdoqId, LinkedComponent[]>;
  /** Component attribute bag indexed for `getFieldAddress`. One entry per
   * focal in startSet — the raw `APIComponentAttributes` from
   * `enhancedScopeData.componentsById`. */
  fieldValuesById: Record<ArdoqId, APIComponentAttributes>;
  /** Names of `SELECT_MULTIPLE_LIST` (multi-list) fields. Used by
   * `getFieldAddress` to know which selected fields should fan a focal
   * out into multiple addresses (ARD-31884). */
  multiListFieldNames: Set<string>;
  /** Main display labels by component id — same source body cells use via
   * `labelsInterface.getComponentDisplayName`. Threaded into
   * `addAddressToGroup` so REFERENCED_COMPONENT group headers show just
   * the main label, not the legacy `Name [field: value]` formatting.
   * ARD-32168. */
  componentLabelById: Record<ArdoqId, string>;
};

const isNonEmptyString = (v: unknown): v is string =>
  typeof v === 'string' && v.length > 0;

const coerceToNonEmptyString = (v: unknown): string | null => {
  if (typeof v === 'string') return v.length > 0 ? v : null;
  if (v === null || v === undefined) return null;
  return String(v);
};

const collectNonEmptyStrings = (values: unknown[]): string[] =>
  values.map(coerceToNonEmptyString).filter(isNonEmptyString);

// List values come from a predefined set with no commas, so splitting a
// raw string on `,` is safe (gridEditor's `fieldValueParser.ts` makes the
// same assumption when round-tripping legacy multi-list values).
const splitCommaSeparated = (value: string): string[] =>
  value
    .split(',')
    .map(s => s.trim())
    .filter(s => s.length > 0);

const parseMultiListValue = (value: unknown): string[] => {
  if (Array.isArray(value)) return collectNonEmptyStrings(value);
  if (!isNonEmptyString(value)) return [];
  // Multi-list values can ship as a JSON-stringified array or a
  // comma-separated string depending on the source.
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return collectNonEmptyStrings(parsed);
  } catch {
    /* fall through to comma split */
  }
  return splitCommaSeparated(value);
};

const getFieldAddress = (
  componentId: ArdoqId,
  fieldNames: string[],
  resolvers: DataResolvers
): (string | null)[][] => {
  const fieldValues = resolvers.fieldValuesById[componentId];
  // Per field, build the list of possible values. `LIST` fields hold a
  // single string (or null); `SELECT_MULTIPLE_LIST` fields hold an array
  // of strings — a component with N values fans out to N addresses on
  // that field axis (ARD-31884). An empty / undefined value collapses to
  // `[null]` so the cartesian product still includes a "no value" slot.
  const perFieldValues: (string | null)[][] = fieldNames.map(name => {
    const value = fieldValues?.[name];
    if (resolvers.multiListFieldNames.has(name)) {
      const values = parseMultiListValue(value);
      return values.length > 0 ? values : [null];
    }
    return [typeof value === 'string' ? value : null];
  });
  // Cartesian product across fields → one HalfAddress per combination.
  return perFieldValues.reduce<(string | null)[][]>(
    (acc, valuesForField) =>
      acc.flatMap(prefix => valuesForField.map(v => [...prefix, v])),
    [[]]
  );
};

type ViewModelAddresses = {
  xFieldAddress: string[];
  xReferenceAddress: string | null;
  /** Inclusive lower bound of the visible hierarchy-level band for the X
   * axis. Anchor addresses are sliced from this absolute level downward
   * (per ARD-31355). */
  xLowLevel: number;
  /** Inclusive upper bound of the visible hierarchy-level band. Anchor
   * addresses are expanded with descendants until the descendant's level
   * equals this. */
  xHighLevel: number;
  yFieldAddress: string[];
  yReferenceAddress: string | null;
  yLowLevel: number;
  yHighLevel: number;
  xReferenceIsOutgoing: boolean;
  yReferenceIsOutgoing: boolean;
  /** Narrowing to a single connected component type per axis (ARD-31884).
   * Undefined ⇒ no narrowing (legacy "all types" behavior). */
  xLinkedComponentTypeName?: string;
  yLinkedComponentTypeName?: string;
  xUseParent: boolean;
  yUseParent: boolean;
};
type ViewModelReducerState = {
  viewModel: ComponentMatrixViewModel;
  addresses: ViewModelAddresses;
  addressFilter: (address: Address) => boolean;
  resolvers: DataResolvers;
};

// if address length is 0, we are showing everything in the "none" row or column.
const isEmptyOrHasNulls = (address: (string | null)[]) =>
  !address.length || address.some(isUndefinedOrNull);
const addressFilter =
  (filterNoneX: boolean, filterNoneY: boolean) => (address: Address) =>
    (!filterNoneX && !filterNoneY) ||
    ((!filterNoneX || !isEmptyOrHasNulls(address.columnValues)) &&
      (!filterNoneY || !isEmptyOrHasNulls(address.rowValues)));

/**
 * True when `focalId` can produce at least one non-null address on the
 * given axis — i.e. its row/column entry in the body would survive
 * `addressFilter` when `filterNoneAxis` is on. Mirrors how
 * `viewModelReducer` combines a focal's field address with its reference
 * anchor addresses (see `combineHalfAddresses`): the survival condition
 * for a single axis is "the combined chain is non-empty and has no nulls".
 *
 * Used to pre-filter `startSet` when collecting anchor depths for the
 * opposite axis's level band, so anchors that only show up in rows/columns
 * filtered out by `showEmptyValues=false` don't inflate `minRef`/`maxRef`.
 */
const focalHasViableAxisContent = (
  focalId: ArdoqId,
  referenceTypeId: string,
  referenceWorkspaceId: string,
  linkedComponentsById: Record<ArdoqId, LinkedComponent[]>,
  selectedFieldNames: string[],
  resolvers: DataResolvers,
  linkedComponentTypeName: string | undefined
): boolean => {
  const fieldAddresses = getFieldAddress(
    focalId,
    selectedFieldNames,
    resolvers
  );
  // With multi-list support (ARD-31884) a focal can produce multiple field
  // halves. The viability check passes as long as at least one of them is
  // entirely non-null (a single non-null fan-out slot keeps the focal
  // visible on this axis).
  const someFieldAddressAllNonNull = fieldAddresses.some(addr =>
    addr.every(v => v !== null)
  );
  const referenceAddresses = referenceTypeId
    ? getLinkedComponentsAddresses(
        focalId,
        referenceTypeId,
        referenceWorkspaceId,
        linkedComponentsById,
        resolvers,
        undefined,
        undefined,
        linkedComponentTypeName
      )
    : [];
  if (referenceAddresses.length === 0) {
    // Combined address = fieldAddress alone. Empty fields → empty address →
    // would be filtered out.
    return (
      selectedFieldNames.length > 0 &&
      fieldAddresses.length > 0 &&
      someFieldAddressAllNonNull
    );
  }
  if (!someFieldAddressAllNonNull) return false;
  // At least one reference-anchor chain must be entirely non-null for the
  // combined `[refChain, ...fieldAddress]` to have no nulls.
  return referenceAddresses.some(addr => addr.every(v => v !== null));
};

const anchorIdsToUniqDepths = (
  anchorIds: Set<string>,
  parentIdById: Record<ArdoqId, ArdoqId | null>
): number[] =>
  uniq(
    [...anchorIds].map(
      id => getComponentHierarchyAddress(id, parentIdById).length - 1
    )
  );

/**
 * Collect the absolute component-tree depth of every anchor (1-hop linked
 * component via `referenceTypeId`) reachable from any focal in `startSet`,
 * deduped by anchor id. Depth is the count of ancestors above the anchor
 * (a root component is depth 0). Feeds `computeDefaultLevelBand`, which
 * derives the `[low, high]` hierarchy-level band the matrix renders.
 */
const collectAnchorLevels = (
  startSet: ArdoqId[],
  referenceTypeId: string,
  referenceWorkspaceId: string,
  linkedComponentsById: Record<ArdoqId, LinkedComponent[]>,
  resolvers: DataResolvers,
  linkedComponentTypeName: string | undefined
): number[] => {
  if (!referenceTypeId) return [];
  const anchorIds = new Set<string>();
  for (const focalId of startSet) {
    for (const addr of getLinkedComponentsAddresses(
      focalId,
      referenceTypeId,
      referenceWorkspaceId,
      linkedComponentsById,
      resolvers,
      undefined,
      undefined,
      linkedComponentTypeName
    )) {
      const anchorId = addr[addr.length - 1];
      if (anchorId && anchorId !== COMPONENT_HIERARCHY_PADDING) {
        anchorIds.add(anchorId);
      }
    }
  }
  return anchorIdsToUniqDepths(anchorIds, resolvers.parentIdById);
};

const focalHasViableParentContent = (
  focalId: ArdoqId,
  parentIdById: Record<ArdoqId, ArdoqId | null>
): boolean => (parentIdById[focalId] ?? null) !== null;

const collectParentAnchorLevels = (
  startSet: ArdoqId[],
  resolvers: DataResolvers
): number[] => {
  const anchorIds = new Set<string>();
  for (const focalId of startSet) {
    const parentId = resolvers.parentIdById[focalId] ?? null;
    if (parentId) anchorIds.add(parentId);
  }
  return anchorIdsToUniqDepths(anchorIds, resolvers.parentIdById);
};

/** Ancestor levels shown above the shallowest anchor in the default band. */
const DEFAULT_LEVEL_BAND_EXTENT = 3;

/**
 * The hierarchy-level band `[low, high]` (absolute component-tree depths) the
 * matrix renders for a set of anchor levels: from the deepest anchor
 * (`maxRef`) up to `DEFAULT_LEVEL_BAND_EXTENT` ancestor levels above the
 * shallowest anchor (`minRef`).
 */
export const computeDefaultLevelBand = (
  anchorLevels: number[]
): [number, number] => {
  if (anchorLevels.length === 0) return [0, 0];
  const minRef = Math.min(...anchorLevels);
  const maxRef = Math.max(...anchorLevels);
  return [Math.max(0, minRef - DEFAULT_LEVEL_BAND_EXTENT), maxRef];
};

const DEFAULT_GROUP_COLORS: GroupColorInfo = {
  fillColor: '',
  isDark: false,
};

const getGroupColors = (
  componentId: string | null | undefined,
  ownerType: ComponentMatrixGroupOwnerType,
  scopeData: EnhancedScopeData,
  filterColorData: FilterColorData
): GroupColorInfo => {
  if (ownerType !== ComponentMatrixGroupOwnerType.REFERENCED_COMPONENT) {
    return DEFAULT_GROUP_COLORS;
  }
  if (!componentId) return DEFAULT_GROUP_COLORS;
  const fillColor =
    componentUtils.getComponentDisplayColorAsSVGAttributes(
      scopeData,
      filterColorData,
      componentId,
      { useAsBackgroundStyle: false }
    ).fill ?? '';
  if (!fillColor) return DEFAULT_GROUP_COLORS;
  return { fillColor, isDark: getLuminance(fillColor) < LUMINANCE_THRESHOLD };
};

const getColumnGroupColors = (
  group: ComponentMatrixGroup,
  scopeData: EnhancedScopeData,
  filterColorData: FilterColorData
): GroupColorInfo =>
  getGroupColors(
    group.address.at(-1),
    group.ownerType,
    scopeData,
    filterColorData
  );

/**
 * Slice an anchor address — `[root, ..., parent, anchor]`, top-down by
 * component-tree depth — to the visible level band `[lowLevel, highLevel]`
 * (ARD-31355). The anchor itself sits at index `addr.length - 1` and at
 * absolute level `addr.length - 1`, so `addr.slice(lowLevel)` keeps every
 * entry from level `lowLevel` through the anchor.
 *
 * When `lowLevel > anchorLevel` (which the default band never produces —
 * `lowLevel ≤ minRef ≤ anchorLevel` always holds — but is still possible if
 * the caller passes a wider band than the anchor reaches), this returns just
 * the anchor entry so the address never becomes empty.
 *
 * Returns both the slice and the source `anchorLevel` so callers can size
 * downstream operations (e.g. `childDegrees`) against the *real* absolute
 * level of the anchor rather than re-deriving it from `lowLevel + slice
 * length - 1`, which is wrong when the defensive branch above fires.
 */
const sliceAnchorAddressByLevelBand = (
  addr: (string | null)[],
  lowLevel: number
): { slice: (string | null)[]; anchorLevel: number } => {
  if (addr.length === 0) return { slice: addr, anchorLevel: -1 };
  const anchorIndex = addr.length - 1;
  const safeLow = Math.max(0, Math.min(lowLevel, anchorIndex));
  return { slice: addr.slice(safeLow), anchorLevel: anchorIndex };
};

/**
 * The referenced ("anchor") component in a half-address: the last non-null,
 * non-padding token of the reference-side (reference tokens precede the
 * `fieldWidth` field-value tokens). Null when the address has no reference
 * anchor on this axis.
 */
export const referenceAnchor = (
  halfAddress: HalfAddress,
  fieldWidth: number
): string | null =>
  halfAddress
    .slice(0, halfAddress.length - fieldWidth)
    .findLast(v => v !== null && v !== COMPONENT_HIERARCHY_PADDING) ?? null;

/**
 * Map from anchor (referenced) component id to the id of the reference of the
 * given type linking `focalId` to that anchor. When several references of the
 * same type reach the same anchor the first wins — the address model already
 * collapses them to a single body-cell position (ARD-32765).
 */
export const buildReferenceIdByAnchor = (
  focalId: ArdoqId,
  referenceTypeId: string,
  linkedComponentsById: Record<ArdoqId, LinkedComponent[]>,
  referenceTypeIdByReferenceId: Record<ArdoqId, string | null>
): Map<string, ArdoqId> => {
  const byAnchor = new Map<string, ArdoqId>();
  for (const { referenceId, componentId: anchorId } of linkedComponentsById[
    focalId
  ] ?? []) {
    if (
      (referenceTypeIdByReferenceId[referenceId] ?? null) === referenceTypeId &&
      !byAnchor.has(anchorId)
    ) {
      byAnchor.set(anchorId, referenceId);
    }
  }
  return byAnchor;
};

const viewModelReducer = (
  state: ViewModelReducerState,
  componentId: ArdoqId
): ViewModelReducerState => {
  const { resolvers } = state;

  const xFieldAddress = getFieldAddress(
    componentId,
    state.addresses.xFieldAddress,
    resolvers
  );
  const { xLowLevel, xHighLevel } = state.addresses;
  const xForwardSlices = state.addresses.xUseParent
    ? getParentAddresses(componentId, resolvers.parentIdById).map(addr =>
        sliceAnchorAddressByLevelBand(addr, xLowLevel)
      )
    : state.addresses.xReferenceAddress
      ? getLinkedComponentsAddresses(
          componentId,
          extractReferenceTypeId(state.addresses.xReferenceAddress),
          extractWorkspaceId(state.addresses.xReferenceAddress),
          state.addresses.xReferenceIsOutgoing
            ? resolvers.targetsById
            : resolvers.sourcesById,
          resolvers,
          undefined,
          undefined,
          state.addresses.xLinkedComponentTypeName
        ).map(addr => sliceAnchorAddressByLevelBand(addr, xLowLevel))
      : [];
  const xForwardAddresses = xForwardSlices.map(({ slice }) => slice);
  const xForwardChildAddresses = xForwardSlices.flatMap(
    ({ slice, anchorLevel }) => {
      const anchorId = slice[slice.length - 1];
      if (!anchorId || anchorId === COMPONENT_HIERARCHY_PADDING) return [];
      // `anchorLevel` is the anchor's *absolute* level in the un-sliced
      // address (== `addr.length - 1` before slicing). We cannot derive it
      // from `xLowLevel + slice.length - 1` because the helper clamps
      // `safeLow` to `anchorIndex` when `lowLevel > anchorIndex`; in that
      // branch the slice drops `anchorIndex` entries, not `xLowLevel`.
      const childDegrees = Math.max(0, xHighLevel - anchorLevel);
      return expandAddressWithChildren(
        slice,
        anchorId,
        childDegrees,
        resolvers.childrenById
      );
    }
  );

  const yFieldAddress = getFieldAddress(
    componentId,
    state.addresses.yFieldAddress,
    resolvers
  );
  const { yLowLevel, yHighLevel } = state.addresses;
  const yForwardSlices = state.addresses.yUseParent
    ? getParentAddresses(componentId, resolvers.parentIdById).map(addr =>
        sliceAnchorAddressByLevelBand(addr, yLowLevel)
      )
    : state.addresses.yReferenceAddress
      ? getLinkedComponentsAddresses(
          componentId,
          extractReferenceTypeId(state.addresses.yReferenceAddress),
          extractWorkspaceId(state.addresses.yReferenceAddress),
          state.addresses.yReferenceIsOutgoing
            ? resolvers.targetsById
            : resolvers.sourcesById,
          resolvers,
          undefined,
          undefined,
          state.addresses.yLinkedComponentTypeName
        ).map(addr => sliceAnchorAddressByLevelBand(addr, yLowLevel))
      : [];
  const yForwardAddresses = yForwardSlices.map(({ slice }) => slice);
  const yForwardChildAddresses = yForwardSlices.flatMap(
    ({ slice, anchorLevel }) => {
      const anchorId = slice[slice.length - 1];
      if (!anchorId || anchorId === COMPONENT_HIERARCHY_PADDING) return [];
      const childDegrees = Math.max(0, yHighLevel - anchorLevel);
      return expandAddressWithChildren(
        slice,
        anchorId,
        childDegrees,
        resolvers.childrenById
      );
    }
  );

  const xData = {
    referencesAddresses: addressSuffixes(xForwardAddresses),
    fieldAddresses: xFieldAddress,
    selectedFieldCount: state.addresses.xFieldAddress.length,
  };
  const xHeaderData = {
    referencesAddresses: [...xForwardAddresses, ...xForwardChildAddresses],
    fieldAddresses: xFieldAddress,
    selectedFieldCount: state.addresses.xFieldAddress.length,
  };
  const yData = {
    referencesAddresses: addressSuffixes(yForwardAddresses),
    fieldAddresses: yFieldAddress,
    selectedFieldCount: state.addresses.yFieldAddress.length,
  };
  const yHeaderData = {
    referencesAddresses: [...yForwardAddresses, ...yForwardChildAddresses],
    fieldAddresses: yFieldAddress,
    selectedFieldCount: state.addresses.yFieldAddress.length,
  };

  const [xAddresses, yAddresses] = [xData, yData].map(
    ({ fieldAddresses, referencesAddresses }) =>
      combineHalfAddresses(fieldAddresses, referencesAddresses)
  );
  const [headerXAddresses, headerYAddresses] = [xHeaderData, yHeaderData].map(
    ({ fieldAddresses, referencesAddresses, selectedFieldCount }) => {
      // permuted reference addresses shouldn't have actual list values appended, otherwise this will create column headers for data that does not exist.
      // Depth is the number of selected fields — a single padding row of
      // that length. `fieldAddresses.length` would be the fan-out count
      // (one entry per multi-list combination), not the depth.
      const nullFieldAddresses = [
        range(selectedFieldCount).map(() => COMPONENT_HIERARCHY_PADDING),
      ];
      const referenceAddressPermutations =
        paddedAddressPermutations(referencesAddresses);
      return [
        ...combineHalfAddresses(fieldAddresses, referencesAddresses),
        // Only add padding header addresses when there are actual reference
        // permutations to combine. When no reference type is selected,
        // referenceAddressPermutations is empty and combineHalfAddresses would
        // fall back to nullFieldAddresses, creating a ghost PADDING column.
        ...(referenceAddressPermutations.length
          ? combineHalfAddresses(
              nullFieldAddresses,
              referenceAddressPermutations
            )
          : []),
      ];
    }
  );

  const [addresses, headerAddresses] = [
    [xAddresses, yAddresses],
    [headerXAddresses, headerYAddresses],
  ].map(([xAddresses, yAddresses]) =>
    yAddresses
      .flatMap(rowValues =>
        xAddresses.map(columnValues => ({
          columnValues,
          rowValues,
        }))
      )
      .filter(state.addressFilter)
  );
  const addressesToUse = addresses.filter(state.addressFilter);

  // #region add addresses, including padded address permutations, to the row and column maps
  // Field depth = number of selected field names (one address-token per field).
  // After multi-list fan-out (ARD-31884), `xFieldAddress` / `yFieldAddress` are
  // `(string|null)[][]` whose `.length` is the cartesian fan-out count, not
  // the depth — use the raw selected-field-names length here instead.
  const xFieldWidth = state.addresses.xFieldAddress.length;
  const yFieldWidth = state.addresses.yFieldAddress.length;
  headerAddresses.forEach(address => {
    const xReferenceWidth = address.columnValues.length - xFieldWidth;
    const yReferenceWidth = address.rowValues.length - yFieldWidth;

    const [xAddressOwnerTypes, yAddressOwnerTypes] = [
      [xReferenceWidth, xFieldWidth],
      [yReferenceWidth, yFieldWidth],
    ].map(([referenceWidth, fieldWidth]) => [
      ...range(referenceWidth).map(
        () => ComponentMatrixGroupOwnerType.REFERENCED_COMPONENT
      ),
      ...range(fieldWidth).map(
        () => ComponentMatrixGroupOwnerType.LIST_FIELD_VALUE
      ),
    ]);

    const xAddressInfo = {
      address: address.columnValues,
      ownerTypes: xAddressOwnerTypes,
      groupMap: state.viewModel.columns,
    };
    const yAddressInfo = {
      address: address.rowValues,
      ownerTypes: yAddressOwnerTypes,
      groupMap: state.viewModel.rows,
    };
    [xAddressInfo, yAddressInfo].forEach(({ address, ownerTypes, groupMap }) =>
      addAddressToGroup(
        address,
        ownerTypes,
        groupMap,
        state.resolvers.componentLabelById
      )
    );
  });
  // #endregion

  // Reference this focal has to each anchor on a reference axis, keyed by
  // anchor id, so a body cell can surface the reference's labels beneath the
  // component's own (ARD-32765). Parent axes carry no reference labels.
  const xReferenceIdByAnchor =
    !state.addresses.xUseParent && state.addresses.xReferenceAddress
      ? buildReferenceIdByAnchor(
          componentId,
          extractReferenceTypeId(state.addresses.xReferenceAddress),
          state.addresses.xReferenceIsOutgoing
            ? resolvers.targetsById
            : resolvers.sourcesById,
          resolvers.referenceTypeIdByReferenceId
        )
      : null;
  const yReferenceIdByAnchor =
    !state.addresses.yUseParent && state.addresses.yReferenceAddress
      ? buildReferenceIdByAnchor(
          componentId,
          extractReferenceTypeId(state.addresses.yReferenceAddress),
          state.addresses.yReferenceIsOutgoing
            ? resolvers.targetsById
            : resolvers.sourcesById,
          resolvers.referenceTypeIdByReferenceId
        )
      : null;

  /** all the row+column addresses for this component id. in other words, all the body cell locations of the component. */
  const componentAddresses = addressesToUse.map(address => {
    const yAnchor = referenceAnchor(address.rowValues, yFieldWidth);
    const xAnchor = referenceAnchor(address.columnValues, xFieldWidth);
    // Row-header reference first, then column-header reference (ARD-32765).
    const referenceIds = uniq(
      [
        yAnchor ? yReferenceIdByAnchor?.get(yAnchor) : undefined,
        xAnchor ? xReferenceIdByAnchor?.get(xAnchor) : undefined,
      ].filter(isTruthy)
    );
    return referenceIds.length > 0
      ? { componentId, address, referenceIds }
      : { componentId, address };
  });
  componentAddresses.forEach(address => {
    state.viewModel.items.push(address);
  });
  return state;
};

type GroupLevelsState = {
  groupsByDepth: ComponentMatrixGroup[][];
  currentDepth: number;
};

const groupLevelsReducer = (
  state: GroupLevelsState,
  currentGroup: ComponentMatrixGroup
): GroupLevelsState => {
  if (state.groupsByDepth.length <= state.currentDepth) {
    state.groupsByDepth.push([]);
  }
  state.groupsByDepth[state.currentDepth].push(currentGroup);
  // Recurse into non-hidden children only.
  // Collapsed groups (isExpanded=false) have isHidden=true children — stopping
  // here means we generate one track for the collapsed group, not N tracks for
  // all its hidden descendants.
  // PADDING groups must NOT be filtered: they occupy grid tracks (counted by
  // buildVisibleLeafCounts / getVisibleItemIndex) and must appear in columnsByDepth
  // so that buildColumnsByDepthWithGridInfo assigns correct column offsets to
  // their siblings.
  const visibleChildren = [...currentGroup.children.values()].filter(
    child => !child.isHidden
  );
  if (visibleChildren.length) {
    visibleChildren.reduce(groupLevelsReducer, {
      ...state,
      currentDepth: state.currentDepth + 1,
    });
  }
  return state;
};

const getGroupsByDepth = (groups: ComponentMatrixGroup[]) =>
  groups.reduce(groupLevelsReducer, {
    groupsByDepth: [],
    currentDepth: 0,
  }).groupsByDepth;

const toggleExpandedGroups = (
  groups: Map<string | null, ComponentMatrixGroup>,
  [address, ...restAddress]: (string | null)[]
) => {
  const group = groups.get(address);
  if (group === undefined) return;

  const shouldToggle = restAddress.length === 0;
  if (shouldToggle) {
    // padding is always expanded, in case there's a list field inside
    group.isExpanded = !group.isExpanded || isComponentHierarchyPadding(group);

    group.children.forEach(child => setHidden(child, !group.isExpanded));

    if (group.isExpanded) {
      // all padding must be expanded.
      expandComponentHierarchyPadding(group);
    }
  } else {
    toggleExpandedGroups(group.children, restAddress);
  }
};
const setHidden = (group: ComponentMatrixGroup, hidden: boolean) => {
  group.isHidden = hidden;
  const iterateChildren = hidden || (!hidden && group.isExpanded); // hide children of hidden groups. show children of expanded groups.
  if (iterateChildren) {
    group.children.forEach(child => setHidden(child, hidden));
  }
};

const expandComponentHierarchyPadding = (group: ComponentMatrixGroup) => {
  [...group.children.values()]
    .filter(child => isComponentHierarchyPadding(child))
    .forEach(child => {
      child.isHidden = false;
      child.isExpanded = true;
      child.children.forEach(child => (child.isHidden = false));
      expandComponentHierarchyPadding(child);
    });
};

const getViewModel = (
  startSet: ArdoqId[],
  viewSettings: ComponentMatrix2ViewSettings,
  axisSources: ComponentMatrix2AxisSources,
  listFields: APIFieldAttributes[],
  sortDependencies: ComponentByIdSortDependencies,
  resolvers: DataResolvers,
  locale: Locale,
  i18nLocale: I18nLocale
) => {
  const {
    fieldNamesX: selectedFieldNamesX,
    fieldNamesY: selectedFieldNamesY,
    referenceX: foundReferenceTypeX,
    referenceY: foundReferenceTypeY,
  } = axisSources;

  const xReferenceAddress =
    foundReferenceTypeX === null
      ? ''
      : formatReferenceType(foundReferenceTypeX);
  const yReferenceAddress =
    foundReferenceTypeY === null
      ? ''
      : formatReferenceType(foundReferenceTypeY);

  const xReferenceTypeId = extractReferenceTypeId(xReferenceAddress);
  const yReferenceTypeId = extractReferenceTypeId(yReferenceAddress);
  const xReferenceWorkspace = extractWorkspaceId(xReferenceAddress);
  const yReferenceWorkspace = extractWorkspaceId(yReferenceAddress);

  const xLinkedComponentsById = viewSettings.referenceTypeX?.isOutgoing
    ? resolvers.targetsById
    : resolvers.sourcesById;
  const yLinkedComponentsById = viewSettings.referenceTypeY?.isOutgoing
    ? resolvers.targetsById
    : resolvers.sourcesById;

  // ARD-31355: collect the absolute component-tree depth of every anchor
  // (1-hop linked component via the axis reference type) so
  // `computeDefaultLevelBand` can derive the `[low, high]` hierarchy-level band
  // the matrix renders. Anchors that share the same id across focals collapse —
  // depths only need to be enumerated once per axis.
  //
  // When `showEmptyValues=false` the body filters out cells whose opposite
  // axis would have null/empty values, so anchors only reachable via such
  // focals don't actually contribute visible rows/columns. Pre-filter the
  // focals to those that can produce a surviving address on the *opposite*
  // axis, so `minRef`/`maxRef` reflect what the user sees rather than every
  // anchor the workspace could in principle expose.
  const filterNoneX =
    !viewSettings.showEmptyValues &&
    (viewSettings.useParentAsX ||
      xReferenceAddress.length !== 0 ||
      selectedFieldNamesX.length !== 0);
  const filterNoneY =
    !viewSettings.showEmptyValues &&
    (viewSettings.useParentAsY ||
      yReferenceAddress.length !== 0 ||
      selectedFieldNamesY.length !== 0);
  const xLinkedComponentTypeName =
    viewSettings.referenceTypeX?.linkedComponentTypeName;
  const yLinkedComponentTypeName =
    viewSettings.referenceTypeY?.linkedComponentTypeName;
  const startSetForX = filterNoneY
    ? startSet.filter(focalId =>
        viewSettings.useParentAsY
          ? focalHasViableParentContent(focalId, resolvers.parentIdById)
          : focalHasViableAxisContent(
              focalId,
              yReferenceTypeId,
              yReferenceWorkspace,
              yLinkedComponentsById,
              selectedFieldNamesY,
              resolvers,
              yLinkedComponentTypeName
            )
      )
    : startSet;
  const startSetForY = filterNoneX
    ? startSet.filter(focalId =>
        viewSettings.useParentAsX
          ? focalHasViableParentContent(focalId, resolvers.parentIdById)
          : focalHasViableAxisContent(
              focalId,
              xReferenceTypeId,
              xReferenceWorkspace,
              xLinkedComponentsById,
              selectedFieldNamesX,
              resolvers,
              xLinkedComponentTypeName
            )
      )
    : startSet;
  const xAnchorLevels = viewSettings.useParentAsX
    ? collectParentAnchorLevels(startSetForX, resolvers)
    : collectAnchorLevels(
        startSetForX,
        xReferenceTypeId,
        xReferenceWorkspace,
        xLinkedComponentsById,
        resolvers,
        xLinkedComponentTypeName
      );
  const yAnchorLevels = viewSettings.useParentAsY
    ? collectParentAnchorLevels(startSetForY, resolvers)
    : collectAnchorLevels(
        startSetForY,
        yReferenceTypeId,
        yReferenceWorkspace,
        yLinkedComponentsById,
        resolvers,
        yLinkedComponentTypeName
      );
  const [xLowLevel, xHighLevel] = computeDefaultLevelBand(xAnchorLevels);
  const [yLowLevel, yHighLevel] = computeDefaultLevelBand(yAnchorLevels);
  const xVisibleLevelCount = Math.max(0, xHighLevel - xLowLevel + 1);
  const yVisibleLevelCount = Math.max(0, yHighLevel - yLowLevel + 1);

  const noColumns =
    xReferenceAddress.length === 0 &&
    selectedFieldNamesX.length === 0 &&
    !viewSettings.useParentAsX;
  const noRows =
    yReferenceAddress.length === 0 &&
    selectedFieldNamesY.length === 0 &&
    !viewSettings.useParentAsY;

  const parentAxisLabel = t(
    i18nLocale,
    'tabview:componentMatrix.leftMenu.parentRelationship',
    'Hierarchy'
  );

  const xAxisDisplayLabel = viewSettings.useParentAsX
    ? (viewSettings.displayTextInViewX ?? parentAxisLabel)
    : getAxisHeaderDisplayLabel(
        viewSettings.displayTextInViewX,
        foundReferenceTypeX,
        selectedFieldNamesX,
        listFields
      );

  const yAxisDisplayLabel = viewSettings.useParentAsY
    ? (viewSettings.displayTextInViewY ?? parentAxisLabel)
    : getAxisHeaderDisplayLabel(
        viewSettings.displayTextInViewY,
        foundReferenceTypeY,
        selectedFieldNamesY,
        listFields
      );

  const viewModel = fillEmptyRowsOrColumns(
    startSet.reduce(viewModelReducer, {
      viewModel: {
        columns: new Map(),
        rows: new Map(),
        items: [],
        columnsByDepth: [],
        rowsByDepth: [],
        columnLevelLabels: [
          ...(viewSettings.useParentAsX
            ? groupLevelLabels(xAxisDisplayLabel, xVisibleLevelCount)
            : foundReferenceTypeX
              ? groupLevelLabels(
                  xAxisDisplayLabel,
                  xVisibleLevelCount,
                  Boolean(foundReferenceTypeX.isOutgoing)
                )
              : []),
          ...(selectedFieldNamesX.length > 0 ? [xAxisDisplayLabel] : []),
        ],
        rowLevelLabels: [
          ...(viewSettings.useParentAsY
            ? groupLevelLabels(yAxisDisplayLabel, yVisibleLevelCount)
            : foundReferenceTypeY
              ? groupLevelLabels(
                  yAxisDisplayLabel,
                  yVisibleLevelCount,
                  Boolean(foundReferenceTypeY.isOutgoing)
                )
              : []),
          ...(selectedFieldNamesY.length > 0 ? [yAxisDisplayLabel] : []),
        ],
        rowModels: [],
        measurements: initialStateMeasurements(),
        hoveredComponentId: null,
        focusedComponentId: null,
        contextComponentId: null,
        legendComponentTypes: [],
      },
      addresses: {
        xFieldAddress: selectedFieldNamesX,
        xReferenceAddress: xReferenceAddress,
        xLowLevel,
        xHighLevel,
        yFieldAddress: selectedFieldNamesY,
        yReferenceAddress: yReferenceAddress,
        yLowLevel,
        yHighLevel,
        xReferenceIsOutgoing: Boolean(foundReferenceTypeX?.isOutgoing),
        yReferenceIsOutgoing: Boolean(foundReferenceTypeY?.isOutgoing),
        xLinkedComponentTypeName,
        yLinkedComponentTypeName,
        xUseParent: viewSettings.useParentAsX,
        yUseParent: viewSettings.useParentAsY,
      },
      addressFilter: addressFilter(
        !viewSettings.showEmptyValues && !noColumns,
        !viewSettings.showEmptyValues && !noRows
      ),
      resolvers,
    }).viewModel
  );

  const fieldByName = (fieldName: string) =>
    listFields.find(listField => listField.name === fieldName);
  const xFields = selectedFieldNamesX.map(fieldByName).filter(isTruthy);
  const yFields = selectedFieldNamesY.map(fieldByName).filter(isTruthy);

  const isCountSort = (s: AxisSortOrder) =>
    s === AxisSortOrder.COUNT_HIGH_LOW || s === AxisSortOrder.COUNT_LOW_HIGH;

  const xSortOrder = viewSettings.sortOrderX ?? AxisSortOrder.DEFAULT;
  const ySortOrder = viewSettings.sortOrderY ?? AxisSortOrder.DEFAULT;

  const xNestedCountMaps = isCountSort(xSortOrder)
    ? buildNestedCountMaps(viewModel.items, Axis.COLUMN)
    : undefined;

  const yNestedCountMaps = isCountSort(ySortOrder)
    ? buildNestedCountMaps(viewModel.items, Axis.ROW)
    : undefined;

  viewModel.columns = sortGroups(
    viewModel.columns,
    xFields,
    sortDependencies,
    xSortOrder,
    locale,
    resolvers.componentLabelById,
    xNestedCountMaps
      ? {
          axis: Axis.COLUMN,
          depth: 0,
          parentAddress: [],
          nestedCountMaps: xNestedCountMaps,
        }
      : undefined
  );
  viewModel.rows = sortGroups(
    viewModel.rows,
    yFields,
    sortDependencies,
    ySortOrder,
    locale,
    resolvers.componentLabelById,
    yNestedCountMaps
      ? {
          axis: Axis.ROW,
          depth: 0,
          parentAddress: [],
          nestedCountMaps: yNestedCountMaps,
        }
      : undefined
  );

  viewModel.items = [...viewModel.items].sort((a, b) =>
    compareComponentsById(a.componentId, b.componentId, sortDependencies)
  );

  return viewModel;
};

const fillEmptyRowsOrColumns = (
  viewModel: ComponentMatrixViewModel
): ComponentMatrixViewModel => {
  const emptyRows = !viewModel.rows.size;
  const emptyColumns = !viewModel.columns.size;
  const rows = emptyRows ? EMPTY_GROUP_MAP : viewModel.rows;
  const columns = emptyColumns ? EMPTY_GROUP_MAP : viewModel.columns;
  const items =
    emptyRows && emptyColumns
      ? viewModel.items.map(item => ({
          ...item,
          address: {
            rowValues: [null],
            columnValues: [null],
          },
        }))
      : emptyRows
        ? viewModel.items.map(item => ({
            ...item,
            address: {
              rowValues: [null],
              columnValues: item.address.columnValues,
            },
          }))
        : emptyColumns
          ? viewModel.items.map(item => ({
              ...item,
              address: {
                rowValues: item.address.rowValues,
                columnValues: [null],
              },
            }))
          : viewModel.items;
  return { ...viewModel, rows, columns, items };
};

const getKeyLabel = (
  key: string,
  isFieldAxis: boolean,
  componentDisplayNameById?: Record<string, string>
): string => {
  if (isFieldAxis) return key;
  return componentDisplayNameById?.[key] ?? key;
};

export const groupKeyComparator =
  (
    listFieldOrder: string[] | undefined,
    sortDependencies: ComponentByIdSortDependencies,
    sortOrder: AxisSortOrder,
    locale: Locale,
    isFieldAxis: boolean,
    countMap?: Map<string | null, number>,
    componentDisplayNameById?: Record<string, string>
  ) =>
  (a: string | null, b: string | null) => {
    if (a === COMPONENT_HIERARCHY_PADDING) return -1;
    if (b === COMPONENT_HIERARCHY_PADDING) return 1;
    if (b === null) return -1;
    if (a === null) return 1;

    switch (sortOrder) {
      case AxisSortOrder.ALPHA_AZ:
        return localeCompareNumeric(
          getKeyLabel(a, isFieldAxis, componentDisplayNameById),
          getKeyLabel(b, isFieldAxis, componentDisplayNameById),
          locale
        );
      case AxisSortOrder.ALPHA_ZA:
        return localeCompareNumeric(
          getKeyLabel(b, isFieldAxis, componentDisplayNameById),
          getKeyLabel(a, isFieldAxis, componentDisplayNameById),
          locale
        );
      case AxisSortOrder.COUNT_HIGH_LOW:
        return (countMap?.get(b) ?? 0) - (countMap?.get(a) ?? 0);
      case AxisSortOrder.COUNT_LOW_HIGH:
        return (countMap?.get(a) ?? 0) - (countMap?.get(b) ?? 0);
      case AxisSortOrder.DEFAULT:
      default:
        if (listFieldOrder) {
          return listFieldOrder.indexOf(a) - listFieldOrder.indexOf(b);
        }
        return compareComponentsById(a, b, sortDependencies);
    }
  };

export const sortGroups = (
  groups: Map<string | null, ComponentMatrixGroup>,
  listFields: APIFieldAttributes[],
  sortDependencies: ComponentByIdSortDependencies,
  sortOrder: AxisSortOrder,
  locale: Locale,
  componentDisplayNameById?: Record<string, string>,
  countSortContext?: CountSortContext
): Map<string | null, ComponentMatrixGroup> => {
  const listField: APIFieldAttributes | undefined = listFields[0];
  const isFieldAxis = listField !== undefined;

  const listFieldOrder =
    sortOrder === AxisSortOrder.DEFAULT
      ? (listField?.defaultValue as string | undefined)?.split(',')
      : undefined;

  const countMap =
    sortOrder === AxisSortOrder.COUNT_HIGH_LOW ||
    sortOrder === AxisSortOrder.COUNT_LOW_HIGH
      ? countSortContext?.nestedCountMaps.get(
          getCountMapLookupKey(
            countSortContext.axis,
            countSortContext.depth,
            countSortContext.parentAddress
          )
        )
      : undefined;

  const sortedKeys = Array.from(groups.keys()).sort(
    groupKeyComparator(
      listFieldOrder,
      sortDependencies,
      sortOrder,
      locale,
      isFieldAxis,
      countMap,
      componentDisplayNameById
    )
  );

  const sortedNonNullKeys = groups.has(null)
    ? [...sortedKeys.filter(key => key !== null), null] // "none" is last.
    : sortedKeys;

  return new Map(
    sortedNonNullKeys.map(key => {
      const group = groups.get(key)!;
      return [
        key,
        {
          ...group,
          children: sortGroups(
            group.children,
            listFields.slice(1),
            sortDependencies,
            sortOrder,
            locale,
            componentDisplayNameById,
            countSortContext
              ? {
                  ...countSortContext,
                  depth: countSortContext.depth + 1,
                  parentAddress: group.address,
                }
              : undefined
          ),
        },
      ];
    })
  );
};

/**
 * Scope-data reference types are read from `model.referenceTypes` as the API sends it, where the
 * Backbone path went through `getReferenceTypesWithDefaults`: it injects `IMPLICIT_REFTYPE_ID` and
 * falls back to all five defaults for a model that declares no reference types. A reference whose
 * type the model omits resolves to nothing here, as it does in every other scope-data view — and
 * `getReferenceTypeInfo` splits its ids out of the resolved type, so both come back `undefined`
 * and `matchSelectedReferenceType` stops matching. A viewpoint or presentation holding that type
 * as its axis reverts to none rather than showing it unnamed.
 */
const resolveReferenceTypeInfo = (
  scopeData: EnhancedScopeData,
  linkedComponent: LinkedComponent
) =>
  getReferenceTypeInfo({
    referenceType: getGlobalReferenceType(
      scopeData,
      linkedComponent.referenceId
    ),
    linkedWorkspaceId:
      scopeData.componentsById[linkedComponent.componentId]?.rootWorkspace ??
      '',
  });

const getReferenceTypes = (
  startSet: ArdoqId[],
  getSourcesOrTargets: (componentId: ArdoqId) => LinkedComponent[],
  enhancedScopeData: EnhancedScopeData
) => {
  // Fan out per `(referenceType, linkedWorkspace, linkedComponentTypeName)`
  // tuple — ARD-31884 wants one dropdown row per connected component type,
  // so different target types reachable via the same reference type render
  // as distinct options the user can pick to narrow the axis to one type.
  const linkedComponents = uniqBy(
    startSet.flatMap(getSourcesOrTargets),
    'referenceId'
  );
  const seenKeys = new Set<string>();
  const referenceTypes = linkedComponents
    .map(linkedComponent => {
      const linkedComponentTypeName =
        enhancedScopeData.componentsById[linkedComponent.componentId]?.type ??
        '';
      return {
        ...resolveReferenceTypeInfo(enhancedScopeData, linkedComponent),
        linkedComponentTypeName,
      };
    })
    .filter(referenceType => {
      const key = `${referenceType.referenceTypeName}|${referenceType.linkedWorkspaceId}|${referenceType.linkedComponentTypeName}`;
      if (seenKeys.has(key)) return false;
      seenKeys.add(key);
      return true;
    });

  const duplicateReferenceTypeNames = new Set(
    findDuplicates(
      referenceTypes,
      (a, b) =>
        a.referenceTypeName === b.referenceTypeName &&
        a.linkedComponentTypeName === b.linkedComponentTypeName
    ).map(({ referenceTypeName }) => referenceTypeName)
  );

  return referenceTypes.map(referenceType => ({
    ...referenceType,
    displayName: duplicateReferenceTypeNames.has(
      referenceType.referenceTypeName
    )
      ? `${referenceType.referenceTypeName} (${
          enhancedScopeData.workspacesById[referenceType.linkedWorkspaceId]
            ?.name ?? ''
        })`
      : referenceType.referenceTypeName,
  }));
};

const getReferenceTypeOptions = (
  startSet: ArdoqId[],
  graphModel: GraphModelShape,
  enhancedScopeData: EnhancedScopeData
) => {
  // graphModel naming describes the focal's role on each edge:
  //   sourceMap.get(focal) → components focal points TO (focal is the edge's
  //     source) → these are *outgoing* references from focal.
  //   targetMap.get(focal) → components that point AT focal (focal is the
  //     edge's target) → these are *incoming* references to focal.
  // This is the inverse of CM1's `componentInterface.getSources/getTargets`
  // naming, which uses focal-centric "who are my sources" semantics — see
  // the sourcesById/targetsById remapping below for the same swap.
  const getOutgoingNeighbors = (id: ArdoqId) =>
    graphModel.sourceMap.get(id) ?? [];
  const getIncomingNeighbors = (id: ArdoqId) =>
    graphModel.targetMap.get(id) ?? [];
  return {
    traversedIncomingReferenceTypes: getReferenceTypes(
      startSet,
      getIncomingNeighbors,
      enhancedScopeData
    ).map(referenceType => ({ ...referenceType, isOutgoing: false })),
    traversedOutgoingReferenceTypes: getReferenceTypes(
      startSet,
      getOutgoingNeighbors,
      enhancedScopeData
    ).map(referenceType => ({ ...referenceType, isOutgoing: true })),
  };
};

export type ComponentMatrix2SourceDataResetArgs = {
  loadedGraph: LoadedGraphWithViewpointMode;
  i18nLocale: I18nLocale;
  orgUsersById: Record<ArdoqId, APIOrganizationUser>;
  sort: ContextSort;
  userLocale: Locale;
  contextComponentId: ArdoqId | null;
  permissionContext: PermissionContext;
  formattingAndLabelRules: FormattingAndLabelRules['rules'];
};

export type ComponentMatrix2ResetViewModelArgs =
  ComponentMatrix2SourceDataResetArgs & {
    viewSettings: ComponentMatrix2ViewSettings;
    onProceedAnyway: VoidFunction;
  };

export type ComponentMatrix2ViewModelArgs =
  ComponentMatrix2ResetViewModelArgs & {
    bypassLimit: boolean;
  };

/**
 * Index components by a depth-first walk of each workspace's tree, children sorted by `_order`
 * and workspaces by name, so sorting by order matches the navigator. Components that the walk
 * doesn't reach are not indexed and fall back to the comparator's tiebreak.
 */
const buildTreeOrderIndex = (
  scopeData: EnhancedScopeData,
  componentIds: ArdoqId[],
  userLocale: Locale
): ReadonlyMap<ArdoqId, number> => {
  const idSet = new Set(componentIds);
  const compareByOrder = getComponentComparator(
    {
      attr: SortAttribute.ORDER,
      order: NumericSortOrder.ASC,
      orderUnsetLast: false,
    },
    userLocale
  );
  const workspaceIds = uniq(
    componentIds
      .map(componentId => componentUtils.getWorkspaceId(scopeData, componentId))
      .filter(isTruthy)
  ).sort((a, b) =>
    localeCompareNumericLowercase(
      scopeDataOperations.getWorkspace(scopeData, a)?.name ?? '',
      scopeDataOperations.getWorkspace(scopeData, b)?.name ?? '',
      userLocale
    )
  );

  const index = new Map<ArdoqId, number>();
  const stack: ArdoqId[] = [];
  for (const workspaceId of workspaceIds) {
    const roots = componentUtils
      .getRootComponents(scopeData, workspaceId)
      .sort(compareByOrder);
    stack.push(...roots.map(({ _id }) => _id).reverse());
    while (stack.length > 0) {
      const componentId = stack.pop()!;
      if (idSet.has(componentId)) {
        index.set(componentId, index.size);
      }
      const children = componentUtils
        .getChildren(scopeData, componentId)
        .sort(compareByOrder);
      stack.push(...children.map(({ _id }) => _id).reverse());
    }
  }
  return index;
};

export const getSortDependencies = (
  scopeData: EnhancedScopeData,
  componentIds: ArdoqId[],
  sort: ContextSort,
  userLocale: Locale
): ComponentByIdSortDependencies => ({
  componentAttributesById: Object.fromEntries(
    componentIds
      .map(componentId =>
        enhancedScopeDataOperations.getComponent(scopeData, componentId)
      )
      .filter(isTruthy)
      .map(component => [component._id, component])
  ),
  sort,
  isDateRangeField: dateRangeOperations.isCombinedDateRangeFieldNames(
    sort.attr
  ),
  userLocale,
  treeOrderIndex:
    sort.attr === SortAttribute.ORDER
      ? buildTreeOrderIndex(scopeData, componentIds, userLocale)
      : undefined,
});

/** The matrix from a loaded graph, before the saved expanded state and sizes are applied. */
export const buildComponentMatrix2SourceData = ({
  viewSettings,
  loadedGraph,
  bypassLimit,
  onProceedAnyway,
  i18nLocale,
  orgUsersById,
  sort,
  userLocale,
  contextComponentId,
  permissionContext,
  formattingAndLabelRules,
}: ComponentMatrix2ViewModelArgs): ComponentMatrix2SourceData => {
  const enhancedScopeData = loadedGraph.scopeData;
  const graphModel = enhancedScopeData.graphData;
  // ARD-31884: when the component-type selector narrows the matrix,
  // drop scope component ids whose type isn't selected. Empty list
  // means "All" — pass everything through (matches behavior for
  // viewpoints saved before the selector was added).
  // Type names present in the loaded scope; also used to source the selector
  // options below.
  const scopeTypeNames = new Set<string>();
  for (const id of loadedGraph.scopeComponentIds) {
    const typeName = enhancedScopeData.componentsById[id]?.type;
    if (typeName) scopeTypeNames.add(typeName);
  }
  // Drop a persisted selection whose type isn't in this dataset (ARD-33213).
  const effectiveSelectedComponentTypes = sanitizeSelectedComponentTypes(
    viewSettings.selectedComponentTypes,
    scopeTypeNames
  );
  const fullStartSet = loadedGraph.scopeComponentIds.filter(componentId => {
    if (effectiveSelectedComponentTypes.length === 0) return true;
    const componentType = enhancedScopeData.componentsById[componentId]?.type;
    return (
      componentType !== undefined &&
      effectiveSelectedComponentTypes.includes(componentType)
    );
  });

  const filterColorData = deriveFilterColorData(
    enhancedScopeData,
    formattingAndLabelRules.filter(isFormattingFilter),
    contextComponentId
      ? enhancedScopeData.componentsById[contextComponentId]
      : undefined
  );

  const errors =
    fullStartSet.length > COMPONENT_MATRIX_COMPONENTS_LIMIT && !bypassLimit
      ? [
          nodeLimitError({
            itemCount: fullStartSet.length,
            limit: COMPONENT_MATRIX_COMPONENTS_LIMIT,
            itemsType: ItemsType.COMPONENTS,
            formatNumber: value => formatNumber(value, 'default', userLocale),
            onProceedAnyway,
            ...getNodeLimitErrorLabels(i18nLocale),
          }),
        ]
      : [];

  const startSet = bypassLimit
    ? fullStartSet
    : fullStartSet.slice(0, COMPONENT_MATRIX_COMPONENTS_LIMIT);

  const { traversedIncomingReferenceTypes, traversedOutgoingReferenceTypes } =
    getReferenceTypeOptions(startSet, graphModel, enhancedScopeData);

  const workspaceIds = uniq(
    startSet
      .map(id => enhancedScopeData.componentsById[id]?.rootWorkspace)
      .filter(isTruthy)
  );
  // Component-type IDs actually present in the start set — used by
  // `getFieldsForViewpoint` to drop fields attached only to component
  // types not in this view. Must use `typeId` (the model-level ID
  // `field.componentType` carries), not `type` (the human-readable
  // name).
  const relevantComponentTypeIds = new Set(
    startSet
      .map(id => enhancedScopeData.componentsById[id]?.typeId)
      .filter(isTruthy)
  );
  const listFields = getFieldsForViewpoint({
    scopeData: enhancedScopeData,
    permissionContext,
    workspaceIds,
    types: [APIFieldType.LIST, APIFieldType.SELECT_MULTIPLE_LIST],
    relevantComponentTypeIds,
  });

  const allFieldTypes = Object.values(APIFieldType).filter(
    t => t !== APIFieldType.USER
  );
  const mergeHeaderFields = (
    fields: FieldWithPermissions[]
  ): FieldWithPermissions[] =>
    dateRangeOperations.mergeDateTimeFieldsToDateRangeFields(fields).fields;
  const linkedTypeIdsForAxis = (
    linkedTypeName: string | undefined
  ): Set<ArdoqId> => {
    if (!linkedTypeName) return new Set();
    return new Set(
      Object.values(enhancedScopeData.componentsById)
        .filter(c => c.type === linkedTypeName && c.typeId)
        .map(c => c.typeId as ArdoqId)
    );
  };
  const additionalHeaderFieldsX = mergeHeaderFields(
    getFieldsForViewpoint({
      scopeData: enhancedScopeData,
      permissionContext,
      workspaceIds,
      types: allFieldTypes,
      relevantComponentTypeIds: linkedTypeIdsForAxis(
        viewSettings.referenceTypeX?.linkedComponentTypeName
      ),
    })
  );
  const additionalHeaderFieldsY = mergeHeaderFields(
    getFieldsForViewpoint({
      scopeData: enhancedScopeData,
      permissionContext,
      workspaceIds,
      types: allFieldTypes,
      relevantComponentTypeIds: linkedTypeIdsForAxis(
        viewSettings.referenceTypeY?.linkedComponentTypeName
      ),
    })
  );

  const hasReferences = startSet.some(
    id =>
      (graphModel.sourceMap.get(id)?.length ?? 0) > 0 ||
      (graphModel.targetMap.get(id)?.length ?? 0) > 0
  );

  // scopeData.components = scope components + their ancestors (the closure
  // rules include the parent chain). Group keys at axis positions can include
  // any of these, so the sort-deps map must cover the full set — startSet
  // alone would miss ancestor group keys and compareComponentsById would
  // silently no-op via its missing-attrs backstop.
  const sortDependencies = getSortDependencies(
    enhancedScopeData,
    loadedGraph.scopeData.components.map(({ _id }) => _id),
    sort,
    userLocale
  );

  const sourcesById: Record<ArdoqId, LinkedComponent[]> = {};
  const targetsById: Record<ArdoqId, LinkedComponent[]> = {};
  const fieldValuesById: Record<ArdoqId, APIComponentAttributes> = {};
  const referenceTypeIdByReferenceId: Record<ArdoqId, string | null> = {};
  for (const focalId of startSet) {
    // `graphModel.sourceMap.get(id)` returns components `id` points TO
    // (`id` is the reference's source), which is what
    // `componentInterface.getTargets(id)` produced in CM1. Likewise
    // `targetMap.get(id)` returns components pointing AT `id`, matching
    // `getSources(id)`. The seam keeps the CM1 sources/targets naming.
    sourcesById[focalId] = graphModel.targetMap.get(focalId) ?? [];
    targetsById[focalId] = graphModel.sourceMap.get(focalId) ?? [];
    const component = enhancedScopeData.componentsById[focalId];
    if (component) {
      fieldValuesById[focalId] = component;
    }
    for (const { referenceId } of [
      ...sourcesById[focalId],
      ...targetsById[focalId],
    ]) {
      const reference = enhancedScopeData.referencesById[referenceId];
      if (reference) {
        referenceTypeIdByReferenceId[referenceId] =
          referenceUtils.toGlobalReferenceTypeId(reference);
      }
    }
  }
  // Inverse parent map → children, derived once from `componentsById`.
  // `getChildren` and `getParentId` lookups in the seam read from these
  // pre-built maps.
  const parentIdById: Record<ArdoqId, ArdoqId | null> = {};
  const childrenById: Record<ArdoqId, ArdoqId[]> = {};
  const workspaceIdByComponentId: Record<ArdoqId, ArdoqId | null> = {};
  const componentTypeByComponentId: Record<ArdoqId, string> = {};
  // Pre-resolved main display names (component.name) — same value body cells
  // get via `labelsInterface.getComponentDisplayName`. Threaded into
  // `addAddressToGroup` so REFERENCED_COMPONENT group headers show just the
  // main label (ARD-32168). Group keys can be any component reached via
  // traversal (startSet + linked components + ancestors), all of which are
  // present in `componentsById`.
  const componentLabelById: Record<ArdoqId, string> = {};
  for (const component of Object.values(enhancedScopeData.componentsById)) {
    parentIdById[component._id] = component.parent ?? null;
    workspaceIdByComponentId[component._id] = component.rootWorkspace ?? null;
    componentTypeByComponentId[component._id] = component.type ?? '';
    componentLabelById[component._id] = component.name ?? '';
    if (component.parent) {
      (childrenById[component.parent] ??= []).push(component._id);
    }
  }
  const multiListFieldNames = new Set(
    listFields
      .filter(field => field.type === APIFieldType.SELECT_MULTIPLE_LIST)
      .map(field => field.name)
  );
  const resolvers: DataResolvers = {
    sourcesById,
    targetsById,
    fieldValuesById,
    parentIdById,
    childrenById,
    workspaceIdByComponentId,
    referenceTypeIdByReferenceId,
    componentTypeByComponentId,
    multiListFieldNames,
    componentLabelById,
  };

  const axisSources = toComponentMatrix2AxisSources({
    viewSettings,
    listFields,
    axisReferences: [
      ...traversedIncomingReferenceTypes,
      ...traversedOutgoingReferenceTypes,
    ],
    additionalHeaderFieldsX,
    additionalHeaderFieldsY,
  });

  const rawViewModel = getViewModel(
    startSet,
    viewSettings,
    axisSources,
    listFields,
    sortDependencies,
    resolvers,
    userLocale,
    i18nLocale
  );

  const hasFilteredNullItems =
    !viewSettings.showEmptyValues &&
    startSet.length !== rawViewModel.items.length;

  // ARD-31884: source the component-type selector dropdown options from
  // the loaded scope set (pre-narrowing), so the user can always
  // re-broaden after picking a single type.
  const availableComponentTypes = [...scopeTypeNames].sort((a, b) =>
    localeCompare(a, b, userLocale)
  );
  // Pre-resolve color + icon per type-name once so the leftMenu can render
  // a chip without needing `enhancedScopeData` (ARD-31884 design pass —
  // image 1 renders the connected component type as a colored badge).
  // Source: every component in `enhancedScopeData.componentsById`, so
  // chips render for *referenced* component types too — those are usually
  // outside `loadedGraph.scopeComponentIds`.
  const componentTypeStylesByName: Record<string, ComponentTypeStyle> = {};
  for (const component of Object.values(enhancedScopeData.componentsById)) {
    const typeName = component.type;
    if (!typeName || componentTypeStylesByName[typeName]) continue;
    componentTypeStylesByName[typeName] = {
      color: enhancedScopeDataOperations.getComponentColor(
        enhancedScopeData,
        component._id
      ),
      representationData:
        enhancedScopeDataOperations.getComponentRepresentationData(
          enhancedScopeData,
          component._id
        ),
    };
  }

  const viewSettingsState: ComponentMatrix2ViewSettingsState = {
    listFields,
    additionalHeaderFieldsX,
    additionalHeaderFieldsY,
    traversedIncomingReferenceTypes,
    traversedOutgoingReferenceTypes,
    quickPerspectiveReferenceTypeFilters: null,
    availableComponentTypes,
    componentTypeStylesByName,
  };

  return {
    viewSettingsState,
    viewSettings,
    settingsBounds: toComponentMatrix2SettingsBounds(viewSettingsState),
    settableViewSettings: toComponentMatrix2StoredSettableSettings({
      viewSettings,
      selectedComponentTypes: effectiveSelectedComponentTypes,
      axisSources,
    }),
    contextComponentId,
    rawViewModel,
    hasReferences,
    hasFilteredNullItems,
    errors,
    i18nLocale,
    orgUsersById,
    errorInfoBoxLabels: getErrorInfoBoxLabels(i18nLocale),
    sort,
    userLocale,
    scopeData: enhancedScopeData,
    loadedGraph,
    permissionContext,
    filterColorData,
    bypassLimit,
    formattingAndLabelRules,
  };
};

/** The shown view model: the source data with the saved expanded state and sizes applied. */
export const expandAndAugmentSourceData = ({
  rawViewModel,
  ...builtState
}: ComponentMatrix2SourceData): ViewpointModeComponentMatrixViewStreamState => {
  const { viewSettings } = builtState;
  const expandedViewModel = {
    ...rawViewModel,
    rows: cloneMap(rawViewModel.rows, cloneGroup),
    columns: cloneMap(rawViewModel.columns, cloneGroup),
  };
  applyPersistedExpandedState(
    expandedViewModel,
    viewSettings.persistedExpandedState
  );
  // Thread userAdjustedColumnWidths and userAdjustedRowHeights onto the
  // viewModel before augmentation so the `measure` step can override the
  // default per-column width / per-row height formulas. augmentViewModel
  // preserves these via its `...viewModel` spread.
  const seededViewModel = {
    ...expandedViewModel,
    userAdjustedColumnWidths: viewSettings.userAdjustedColumnWidths,
    userAdjustedRowHeights: viewSettings.userAdjustedRowHeights,
    userAdjustedRowHeadersWidth: viewSettings.userAdjustedRowHeadersWidth,
  };
  return {
    ...builtState,
    viewModel: augmentViewModelFromState(builtState, seededViewModel),
  };
};

/** The whole view model from a loaded graph, which is what the stream's reset does with what its
 *  sources hand it. Exported so a test or a story can drive the pass. */
export const buildComponentMatrix2ViewModel = (
  args: ComponentMatrix2ViewModelArgs
): ViewpointModeComponentMatrixViewStreamState => {
  const transaction = profiling.startTransaction(
    'component matrix 2 reset view model',
    500,
    profiling.Team.INSIGHT
  );
  const result = expandAndAugmentSourceData(
    buildComponentMatrix2SourceData(args)
  );
  profiling.endTransaction(transaction, {
    viewId: ViewIds.COMPONENT_MATRIX_2,
    metadata: { itemCount: result.viewModel.items.length },
  });
  return result;
};

const getAdditionalHeaderFieldsSpec = (state: ComponentMatrix2BuiltState) => ({
  fieldNamesX: state.viewSettings.additionalHeaderFieldNamesX,
  fieldsX: state.viewSettingsState.additionalHeaderFieldsX.filter(f =>
    state.viewSettings.additionalHeaderFieldNamesX.includes(f.name)
  ),
  fieldNamesY: state.viewSettings.additionalHeaderFieldNamesY,
  fieldsY: state.viewSettingsState.additionalHeaderFieldsY.filter(f =>
    state.viewSettings.additionalHeaderFieldNamesY.includes(f.name)
  ),
});

const augmentViewModelFromState = (
  state: ComponentMatrix2BuiltState,
  viewModel: AugmentViewModelArgs['viewModel']
): ViewpointModeComponentMatrixViewModel =>
  augmentViewModel({
    ...state,
    viewModel,
    additionalHeaderFields: getAdditionalHeaderFieldsSpec(state),
    colorByComponent: state.viewSettings.colorRowColumnHeaderByComponent,
  });

export const reduceCollapseGroup = <
  State extends ViewpointModeComponentMatrixViewStreamState,
>(
  state: State,
  {
    address,
    dimension,
  }: ExtractPayload<typeof componentMatrixToggleCollapseGroup>
): State => {
  const newViewModel = { ...state.viewModel };
  if (dimension === ComponentMatrixDimension.COLUMN) {
    const newColumns = cloneMap(newViewModel.columns, cloneGroup);
    toggleExpandedGroups(newColumns, address);
    newViewModel.columns = newColumns;
  } else if (dimension === ComponentMatrixDimension.ROW) {
    const newRows = cloneMap(newViewModel.rows, cloneGroup);
    toggleExpandedGroups(newRows, address);
    newViewModel.rows = newRows;
  }
  return {
    ...state,
    viewModel: augmentViewModelFromState(state, newViewModel),
  };
};

const expandGroupsRecursive = (
  groups: Map<string | null, ComponentMatrixGroup>
) => {
  groups.forEach(group => {
    group.isHidden = false;
    group.isExpanded = true;
    expandGroupsRecursive(group.children);
  });
};

export const collapseAllGroups = (
  groups: Map<string | null, ComponentMatrixGroup>
): void => {
  groups.forEach(group => {
    // Padding stays expanded so its real child level can still be revealed.
    if (!isComponentHierarchyPadding(group)) {
      group.isExpanded = false;
    }
    group.children.forEach(child => {
      child.isHidden = true;
    });
    collapseAllGroups(group.children);
  });
};

export const reduceCollapseAll = <
  State extends ViewpointModeComponentMatrixViewStreamState,
>(
  state: State
): State => {
  const newViewModel = { ...state.viewModel };
  const newColumns = cloneMap(newViewModel.columns, cloneGroup);
  const newRows = cloneMap(newViewModel.rows, cloneGroup);
  collapseAllGroups(newColumns);
  collapseAllGroups(newRows);
  return {
    ...state,
    viewModel: augmentViewModelFromState(state, {
      ...newViewModel,
      columns: newColumns,
      rows: newRows,
    }),
  };
};

export const reduceExpandAll = <
  State extends ViewpointModeComponentMatrixViewStreamState,
>(
  state: State
): State => {
  const newViewModel = { ...state.viewModel };
  const newColumns = cloneMap(newViewModel.columns, cloneGroup);
  expandGroupsRecursive(newColumns);
  const newRows = cloneMap(newViewModel.rows, cloneGroup);
  expandGroupsRecursive(newRows);
  return {
    ...state,
    viewModel: augmentViewModelFromState(state, {
      ...newViewModel,
      columns: newColumns,
      rows: newRows,
    }),
  };
};

export const reduceCollapseAllRows = <
  State extends ViewpointModeComponentMatrixViewStreamState,
>(
  state: State
): State => {
  const newViewModel = { ...state.viewModel };
  const newRows = cloneMap(newViewModel.rows, cloneGroup);
  collapseAllGroups(newRows);
  return {
    ...state,
    viewModel: augmentViewModelFromState(state, {
      ...newViewModel,
      rows: newRows,
    }),
  };
};

export const reduceExpandAllRows = <
  State extends ViewpointModeComponentMatrixViewStreamState,
>(
  state: State
): State => {
  const newViewModel = { ...state.viewModel };
  const newRows = cloneMap(newViewModel.rows, cloneGroup);
  expandGroupsRecursive(newRows);
  return {
    ...state,
    viewModel: augmentViewModelFromState(state, {
      ...newViewModel,
      rows: newRows,
    }),
  };
};

export const reduceCollapseAllColumns = <
  State extends ViewpointModeComponentMatrixViewStreamState,
>(
  state: State
): State => {
  const newViewModel = { ...state.viewModel };
  const newColumns = cloneMap(newViewModel.columns, cloneGroup);
  collapseAllGroups(newColumns);
  return {
    ...state,
    viewModel: augmentViewModelFromState(state, {
      ...newViewModel,
      columns: newColumns,
    }),
  };
};

export const reduceExpandAllColumns = <
  State extends ViewpointModeComponentMatrixViewStreamState,
>(
  state: State
): State => {
  const newViewModel = { ...state.viewModel };
  const newColumns = cloneMap(newViewModel.columns, cloneGroup);
  expandGroupsRecursive(newColumns);
  return {
    ...state,
    viewModel: augmentViewModelFromState(state, {
      ...newViewModel,
      columns: newColumns,
    }),
  };
};

export const handleHoveredComponentChanged = <
  State extends ViewpointModeComponentMatrixViewStreamState,
>(
  state: State,
  hoveredComponentId: ArdoqId | null
): State => ({
  ...state,
  viewModel: {
    ...state.viewModel,
    hoveredComponentId,
  },
});

export const handleFocusedComponentChanged = <
  State extends ViewpointModeComponentMatrixViewStreamState,
>(
  state: State,
  focusedComponentId: ArdoqId | null
): State => ({
  ...state,
  viewModel: {
    ...state.viewModel,
    focusedComponentId:
      focusedComponentId !== state.viewModel.focusedComponentId
        ? focusedComponentId
        : null,
    hoveredComponentId: null,
  },
});

const leafColumnAddressKey = (address: (string | null)[]): string =>
  address.map(v => (v === null ? '\0' : v)).join('|');

const getLeafColumnKeys = (
  viewModel: ViewpointModeComponentMatrixViewModel
): string[] => {
  const leaves: { address: (string | null)[]; gridColumn: number }[] = [];
  for (const level of viewModel.columnsByDepthWithGridInfo) {
    for (const group of level) {
      const hasVisibleChildren =
        group.isExpanded && [...group.children.values()].some(c => !c.isHidden);
      if (!hasVisibleChildren) {
        leaves.push({ address: group.address, gridColumn: group.gridColumn });
      }
    }
  }
  leaves.sort((a, b) => a.gridColumn - b.gridColumn);
  return leaves.map(l => leafColumnAddressKey(l.address));
};

/**
 * Stable per-row keys aligned with `rowHeights`. Each rowModel maps to one
 * grid row, identified by its innermost (leaf) header cell's group address.
 * Keys survive expand/collapse and other layout changes that re-render rows
 * at different indices, so `userAdjustedRowHeights[key]` keeps following the
 * same row.
 */
const getLeafRowKeys = (rowModels: RowElementModel[]): string[] =>
  rowModels.map(rowModel => {
    const leafCell = rowModel.headerCells[rowModel.headerCells.length - 1];
    return leafCell ? leafColumnAddressKey(leafCell.group.address) : '';
  });

const augmentListValueCellLabelLineCount = (
  rowModels: RowElementModel[],
  rowHeadersAreaWidth: number
): RowElementModel[] =>
  rowModels.map(rowModel => ({
    ...rowModel,
    headerCells: rowModel.headerCells.map(headerCell => {
      if (
        headerCell.group.isHidden ||
        isComponentHierarchyPadding(headerCell.group) ||
        headerCell.group.ownerType ===
          ComponentMatrixGroupOwnerType.REFERENCED_COMPONENT
      ) {
        return headerCell;
      }
      const label = headerCell.group.address.at(-1) ?? NONE_HEADER_LABEL;
      return {
        ...headerCell,
        labelLineCount: getListValueRowHeaderLineCount(
          label,
          rowHeadersAreaWidth
        ),
      };
    }),
  }));

const augmentComponentRowLabelLineCount = (
  rowModels: RowElementModel[],
  rowHeadersAreaWidth: number
): RowElementModel[] =>
  rowModels.map(rowModel => ({
    ...rowModel,
    headerCells: rowModel.headerCells.map(headerCell => {
      if (
        headerCell.group.isHidden ||
        isComponentHierarchyPadding(headerCell.group) ||
        headerCell.group.ownerType !==
          ComponentMatrixGroupOwnerType.REFERENCED_COMPONENT
      ) {
        return headerCell;
      }
      const level = headerCell.indexAddress.length - 1;
      const lineCount = getComponentRowHeaderLineCount(
        headerCell.group,
        level,
        rowHeadersAreaWidth
      );
      const needsStripeHeight =
        level > 0 &&
        headerCell.group.isExpanded &&
        hasChildren(headerCell.group);
      const cardWidth =
        rowHeadersAreaWidth - 2 * CARD_GROUP_HORIZONTAL_MARGIN * level;
      const otherLabelWrappedLines =
        headerCell.group.otherLabels.length > 0
          ? computeRowHeaderOtherLabelWrappedLines(
              headerCell.group.otherLabels,
              cardWidth
            )
          : undefined;
      return {
        ...headerCell,
        labelLineCount: lineCount,
        ...(needsStripeHeight
          ? {
              stripeHeight: getComponentRowHeaderHeight(
                headerCell.group,
                level,
                rowHeadersAreaWidth,
                lineCount
              ),
            }
          : {}),
        ...(otherLabelWrappedLines !== undefined
          ? { otherLabelWrappedLines }
          : {}),
      };
    }),
  }));

/**
 * Tags each row's header cells with `isMinHeight`: true when the row's allocated
 * height equals what the row header itself needs (the header is the binding
 * constraint), false when body content or a user resize stretched the row
 * taller. The renderer centers the label at min height and top-aligns it once
 * the row grows past it, so the label stays visible (ARD-32762). `rowHeights`
 * already includes `rowBottomGap`, so it is subtracted before comparing against
 * the row-header height.
 */
export const augmentHeaderCellsWithIsMinHeight = (
  rowModels: RowElementModel[],
  rowHeights: number[],
  rowHeaderHeights: number[]
): RowElementModel[] =>
  rowModels.map((rowModel, index) => {
    const allocatedContentHeight =
      (rowHeights[index] ?? 0) - (rowModel.rowBottomGap ?? 0);
    const isMinHeight =
      allocatedContentHeight <= (rowHeaderHeights[index] ?? 0);
    return {
      ...rowModel,
      headerCells: rowModel.headerCells.map(headerCell => ({
        ...headerCell,
        isMinHeight,
      })),
    };
  });

type MeasureResult = {
  measurements: ViewpointModeComponentMatrixMeasurements;
  rowModels: RowElementModel[];
};

const measure = (
  viewModel: ViewpointModeComponentMatrixViewModel
): MeasureResult => {
  const transaction = profiling.startTransaction(
    'component matrix 2 measure cells',
    200,
    profiling.Team.INSIGHT
  );
  const { rowModels } = viewModel;
  const columnLevelLabelWidths =
    viewModel.columnLevelLabels.map(measureLevelLabel);
  const widestColumnLevelLabel =
    columnLevelLabelWidths.length === 0
      ? 0
      : Math.max(...columnLevelLabelWidths);

  const levelHeadersAreaLeftColumnWidth =
    viewModel.rowLevelLabels.length === 0
      ? 0
      : Math.min(
          LEVEL_HEADER_CARD_MAX_WIDTH,
          Math.max(
            LEVEL_HEADER_CARD_MIN_WIDTH,
            measureRowLevelHeaders(viewModel.rowLevelLabels)
          )
        );
  const levelHeadersAreaRightColumnWidth =
    widestColumnLevelLabel === 0
      ? 0
      : Math.min(
          LEVEL_HEADER_CARD_MAX_WIDTH,
          Math.max(
            LEVEL_HEADER_CARD_MIN_WIDTH,
            widestColumnLevelLabel + 2 * LEVEL_HEADER_CELL_HORIZONTAL_PADDING
          )
        );
  const numLevelHeaderCards =
    (levelHeadersAreaLeftColumnWidth > 0 ? 1 : 0) +
    (levelHeadersAreaRightColumnWidth > 0 ? 1 : 0);
  const levelHeadersAreaTotalWidth =
    numLevelHeaderCards === 0
      ? 0
      : levelHeadersAreaLeftColumnWidth +
        levelHeadersAreaRightColumnWidth +
        (numLevelHeaderCards - 1) * LEVEL_HEADERS_CARD_GAP +
        LEVEL_HEADERS_CONTAINER_HORIZONTAL_PADDING;
  const rowHeadersAreaMinWidth = levelHeadersAreaTotalWidth;

  // When the user has resized the row-headers area, measure label wrapping at
  // the effective rendered width rather than the auto content width so row
  // heights stay consistent with what is actually visible (ARD-32764).
  const effectiveMeasurementWidth =
    viewModel.userAdjustedRowHeadersWidth !== undefined
      ? Math.max(rowHeadersAreaMinWidth, viewModel.userAdjustedRowHeadersWidth)
      : undefined;

  const cellSizes = measureCells(rowModels);
  const rowHeaderSizes = measureRowHeaders(
    rowModels,
    effectiveMeasurementWidth
  );

  const columnWidths =
    cellSizes.length === 0
      ? []
      : range(cellSizes[0].length).map(columnIndex =>
          Math.max(...cellSizes.map(row => row[columnIndex].width))
        );

  const columnHeaderWidths = getHeaderColumnWidths(viewModel);
  // MIN_COLUMN_HEADER_WIDTH is already applied per-group (before colSpan division)
  // in getHeaderColumnWidths, so applying it per-leaf here would over-expand
  // collapsed groups (e.g. a 10-leaf collapsed column would get 36px × 10 = 360px
  // instead of the ~122px the header actually needs).
  // Per ARD-31416, when a column has a user-adjusted width we discard the
  // MAX_COLUMN_HEADER_WIDTH cap (the spec lifts the "even width" + "max width"
  // limits once the user adjusts) but still clamp to the measured minimum so
  // labels never truncate below their content width.
  const leafColumnKeys = getLeafColumnKeys(viewModel);
  // Collapse empty hierarchy-padding columns to a thin stripe (ARD-32761), even
  // when an ancestor group's label width was distributed onto their track. A
  // padding column that holds its parent's direct members (non-empty body, wider
  // than the thin stripe) keeps its normal width.
  const paddingTracks = getPaddingTracks(viewModel);
  const leafColumnMinWidths = columnHeaderWidths.map(
    (columnHeaderWidth, index) =>
      paddingTracks[index] && columnWidths[index] <= EMPTY_COLUMN_WIDTH
        ? EMPTY_COLUMN_WIDTH
        : Math.max(columnHeaderWidth, columnWidths[index])
  );
  // Fall back to {} for the legacy `ComponentMatrixViewModel` arm of
  // augmentViewModel's union (the storybook fixtures don't carry it).
  const userAdjustedColumnWidths = viewModel.userAdjustedColumnWidths ?? {};
  const columnHeaderAndColumnWidths = leafColumnMinWidths.map(
    (measuredMin, index) => {
      const userAdjusted =
        userAdjustedColumnWidths[leafColumnKeys[index] ?? ''];
      if (userAdjusted !== undefined) {
        return Math.max(measuredMin, userAdjusted);
      }
      return Math.min(MAX_COLUMN_HEADER_WIDTH, measuredMin);
    }
  );

  const rowBodyHeights = cellSizes.map(row =>
    Math.max(...row.map(cell => cell.height))
  );
  const rowHeaderHeights = rowHeaderSizes.map(row => row.height);

  // Per-row content min: max of body cell content height and row-header height.
  // The bottom gap is encoded into the grid track on top of that, both for the
  // measured min and for any user-adjusted height (so the inter-root-group
  // visual gap is preserved regardless of resize).
  const leafRowMinHeights = rowBodyHeights.map((rowBodyHeight, index) =>
    Math.max(rowBodyHeight, rowHeaderHeights[index] ?? 0)
  );
  const leafRowKeys = getLeafRowKeys(rowModels);
  // Fall back to {} for the legacy `ComponentMatrixViewModel` arm of
  // augmentViewModel's union (the storybook fixtures don't carry it).
  const userAdjustedRowHeights = viewModel.userAdjustedRowHeights ?? {};
  const rowHeights = leafRowMinHeights.map((measuredMin, index) => {
    const userAdjusted = userAdjustedRowHeights[leafRowKeys[index] ?? ''];
    const contentHeight =
      userAdjusted !== undefined
        ? Math.max(measuredMin, userAdjusted)
        : measuredMin;
    return contentHeight + (rowModels[index]?.rowBottomGap ?? 0);
  });

  const autoRowHeadersAreaWidth = Math.min(
    MAX_ROW_HEADER_WIDTH,
    Math.max(...rowHeaderSizes.map(rowHeaderSize => rowHeaderSize.width))
  );
  const autoLeftColumnWidth = Math.max(
    levelHeadersAreaTotalWidth,
    autoRowHeadersAreaWidth
  );
  const mainContainerLeftColumnWidth =
    effectiveMeasurementWidth !== undefined
      ? Math.max(rowHeadersAreaMinWidth, effectiveMeasurementWidth)
      : autoLeftColumnWidth;

  // columnsByDepth is already computed and stored by augmentWithGroupsByDepth.
  const { columnsByDepth } = viewModel;
  const columnHeaderLevelStripeHeights =
    getColumnHeaderLevelStripeHeights(columnsByDepth);
  const columnHeaderLevelHeights = getColumnHeaderLevelHeights(columnsByDepth);
  const columnHeadersAreaHeight = columnsByDepth.reduce(
    (sum, level, index) =>
      levelHasRenderableContent(level)
        ? sum + columnHeaderLevelHeights[index]
        : sum,
    0
  );

  const measurements = {
    mainContainerLeftColumnWidth,
    columnHeaderWidths,
    columnHeaderAndColumnWidths,
    rowHeights,
    columnHeadersAreaHeight,
    columnHeaderLevelStripeHeights,
    columnHeadersAreaGridTemplateRows: columnHeadersAreaGridTemplateRows(
      columnsByDepth,
      columnHeaderLevelHeights
    ),
    leafColumnKeys,
    leafColumnMinWidths,
    leafRowKeys,
    leafRowMinHeights,
    rowHeadersAreaMinWidth,
  };
  profiling.endTransaction(transaction, {
    viewId: ViewIds.COMPONENT_MATRIX_2,
    metadata: {
      rowCount: rowModels.length,
      colCount: rowModels[0]?.bodyCells.length ?? 0,
    },
  });
  return {
    measurements,
    rowModels: augmentBodyCellItemsWithOtherLabelWrappedLines(
      augmentHeaderCellsWithIsMinHeight(
        effectiveMeasurementWidth !== undefined
          ? augmentComponentRowLabelLineCount(
              augmentListValueCellLabelLineCount(
                rowModels,
                effectiveMeasurementWidth
              ),
              effectiveMeasurementWidth
            )
          : augmentListValueCellLabelLineCount(
              rowModels,
              autoRowHeadersAreaWidth
            ),
        rowHeights,
        rowHeaderHeights
      )
    ),
  };
};

const buildOtherLabels = (
  componentId: string,
  selectedFieldNames: string[],
  customFields: FieldWithPermissions[],
  scopeData: EnhancedScopeData,
  i18nLocale: I18nLocale
): OtherLabel[] =>
  selectedFieldNames.flatMap((fieldName): OtherLabel[] => {
    if (fieldName === ComponentLabelSource.TYPE) {
      const typeName = scopeData.componentsById[componentId]?.type ?? null;
      return typeName
        ? [
            [
              t(
                i18nLocale,
                'tabview:componentMatrix2.axis.additionalFields.componentType',
                'Component type'
              ),
              typeName,
              true,
              newConditionalFormattingMultiLabelStyle,
            ],
          ]
        : [];
    }
    if (fieldName === COMPONENT_OR_REFERENCE_DESCRIPTION_FIELD_LABEL) {
      const description = fieldValueFormatters.formatTextAreaFieldValue(
        scopeData.componentsById[componentId]?.description
      );
      return description
        ? [
            [
              t(
                i18nLocale,
                'tabview:componentMatrix2.axis.additionalFields.description',
                'Description'
              ),
              description,
              true,
              newConditionalFormattingMultiLabelStyle,
            ],
          ]
        : [];
    }
    const field = customFields.find(f => f.name === fieldName);
    if (!field) return [];
    const value = fieldUtils.getFieldValueForComponentOrReference(
      scopeData,
      componentId,
      field.name
    );
    const formatted = fieldUtils.fieldAttributesToLabel({
      fieldAttributes: field,
      value,
      i18nLocale,
      excludeTime: true,
      formatUserFieldFn: val => (typeof val === 'string' ? val : null),
    });
    if (!formatted) return [];
    return [
      [
        field.label ?? field.name,
        formatted,
        true,
        newConditionalFormattingMultiLabelStyle,
      ] satisfies OtherLabel,
    ];
  });

const populateGroupsOtherLabels = (
  groups: Map<string | null, ComponentMatrixGroup>,
  selectedFieldNames: string[],
  selectedFields: FieldWithPermissions[],
  scopeData: EnhancedScopeData,
  i18nLocale: I18nLocale
): Map<string | null, ComponentMatrixGroup> => {
  if (selectedFieldNames.length === 0) {
    return resetGroupsOtherLabels(groups);
  }
  return new Map(
    [...groups.entries()].map(([key, group]) => {
      const otherLabels =
        group.ownerType === ComponentMatrixGroupOwnerType.REFERENCED_COMPONENT
          ? (() => {
              const componentId = group.address.at(-1);
              return componentId
                ? buildOtherLabels(
                    componentId,
                    selectedFieldNames,
                    selectedFields,
                    scopeData,
                    i18nLocale
                  )
                : [];
            })()
          : group.otherLabels;
      return [
        key,
        {
          ...group,
          otherLabels,
          children: populateGroupsOtherLabels(
            group.children,
            selectedFieldNames,
            selectedFields,
            scopeData,
            i18nLocale
          ),
        },
      ];
    })
  );
};

const resetGroupsOtherLabels = (
  groups: Map<string | null, ComponentMatrixGroup>
): Map<string | null, ComponentMatrixGroup> =>
  new Map(
    [...groups.entries()].map(([key, group]) => [
      key,
      {
        ...group,
        otherLabels: [],
        children: resetGroupsOtherLabels(group.children),
      },
    ])
  );

const augmentWithGroupsByDepth = (
  viewModel: ComponentMatrixViewModel | ViewpointModeComponentMatrixViewModel,
  scopeData: EnhancedScopeData,
  filterColorData: FilterColorData,
  selectedHeaderFieldNamesX: string[],
  selectedHeaderFieldsX: FieldWithPermissions[],
  selectedHeaderFieldNamesY: string[],
  selectedHeaderFieldsY: FieldWithPermissions[],
  i18nLocale: I18nLocale,
  colorByComponent: boolean
): ViewpointModeComponentMatrixViewModel => {
  const columns = populateGroupsOtherLabels(
    viewModel.columns,
    selectedHeaderFieldNamesX,
    selectedHeaderFieldsX,
    scopeData,
    i18nLocale
  );
  const rows = populateGroupsOtherLabels(
    viewModel.rows,
    selectedHeaderFieldNamesY,
    selectedHeaderFieldsY,
    scopeData,
    i18nLocale
  );
  const columnsByDepth: ComponentMatrix2Group[][] = getGroupsByDepth([
    ...columns.values(),
  ]).map(level =>
    level.map(group => ({
      ...group,
      ...(colorByComponent
        ? getColumnGroupColors(group, scopeData, filterColorData)
        : {
            fillColor: colors.grey400,
            borderColor: colors.grey200,
            isDark: false,
          }),
    }))
  );
  const columnsByDepthWithGridInfo = buildColumnsByDepthWithGridInfo(
    columnsByDepth[0] ?? [],
    new Map(
      columnsByDepth.flat().map(group => [group.address.at(-1) ?? '', group])
    )
  ).map(level => {
    // A list-value column header is at its level's minimum height when its
    // label is as tall as the tallest in the level; a shorter one leaves slack
    // the renderer top-aligns into (ARD-32762).
    const maxLineCount = level.reduce(
      (max, group) => Math.max(max, getMaxWrappedLinesForColumn(group)),
      1
    );
    return level.map(group => {
      const representationData = getReferencedComponentRepresentationData(
        scopeData,
        group.ownerType,
        group.address.at(-1) ?? undefined
      );
      const isMinHeight = getMaxWrappedLinesForColumn(group) >= maxLineCount;
      return representationData === undefined
        ? { ...group, isMinHeight }
        : { ...group, representationData, isMinHeight };
    });
  });
  return {
    ...(viewModel as ViewpointModeComponentMatrixViewModel),
    columns,
    rows,
    columnsByDepth,
    columnsByDepthWithGridInfo,
    rowsByDepth: getGroupsByDepth([...rows.values()]),
  };
};
type AugmentViewModelArgs = {
  viewModel: ComponentMatrixViewModel | ViewpointModeComponentMatrixViewModel;
  i18nLocale: I18nLocale;
  orgUsersById: Record<ArdoqId, APIOrganizationUser>;
  contextComponentId: ArdoqId | null;
  scopeData: EnhancedScopeData;
  filterColorData: FilterColorData;
  formattingAndLabelRules: FormattingAndLabelRules['rules'];
  additionalHeaderFields?: {
    fieldNamesX: string[];
    fieldsX: FieldWithPermissions[];
    fieldNamesY: string[];
    fieldsY: FieldWithPermissions[];
  };
  colorByComponent?: boolean;
};
export const augmentViewModel = ({
  viewModel,
  i18nLocale,
  orgUsersById,
  contextComponentId,
  scopeData,
  filterColorData,
  formattingAndLabelRules,
  additionalHeaderFields,
  colorByComponent = true,
}: AugmentViewModelArgs): ViewpointModeComponentMatrixViewModel =>
  augmentWithMeasurements(
    augmentWithRowModels(
      augmentWithGroupsByDepth(
        viewModel,
        scopeData,
        filterColorData,
        additionalHeaderFields?.fieldNamesX ?? [],
        additionalHeaderFields?.fieldsX ?? [],
        additionalHeaderFields?.fieldNamesY ?? [],
        additionalHeaderFields?.fieldsY ?? [],
        i18nLocale,
        colorByComponent
      ),
      i18nLocale,
      orgUsersById,
      contextComponentId,
      scopeData,
      filterColorData,
      colorByComponent,
      formattingAndLabelRules
    )
  );
const augmentWithRowModels = (
  // Called after augmentWithGroupsByDepth, so columnsByDepth is already set.
  viewModel: ViewpointModeComponentMatrixViewModel,
  i18nLocale: I18nLocale,
  orgUsersById: Record<ArdoqId, APIOrganizationUser>,
  contextComponentId: ArdoqId | null,
  scopeData: EnhancedScopeData,
  filterColorData: FilterColorData,
  colorByComponent: boolean,
  formattingAndLabelRules: FormattingAndLabelRules['rules']
): ViewpointModeComponentMatrixViewModel => ({
  ...viewModel,
  measurements: {
    ...viewModel.measurements,
    columnHeadersAreaHeight: 0,
    columnHeaderLevelStripeHeights: [],
    columnHeadersAreaGridTemplateRows: '',
  },
  rowModels: toViewpointRowModels(
    viewModel,
    i18nLocale,
    orgUsersById,
    contextComponentId,
    scopeData,
    filterColorData,
    colorByComponent,
    formattingAndLabelRules
  ),
  legendComponentTypes: computeLegendComponentTypes(viewModel.items),
});
const toResizeHandles = (
  measurements: ViewpointModeComponentMatrixMeasurements
): ColumnResizeHandleModel[] => {
  const {
    columnHeaderAndColumnWidths,
    leafColumnKeys,
    leafColumnMinWidths,
    rowHeights,
  } = measurements;
  if (columnHeaderAndColumnWidths.length <= 1) return [];
  const gridRowEnd = rowHeights.length + 1;
  return columnHeaderAndColumnWidths.slice(0, -1).flatMap((width, index) => {
    const columnKey = leafColumnKeys[index];
    if (columnKey === undefined) return [];
    return [
      {
        columnKey,
        gridColumn: index + 1,
        gridRowEnd,
        currentWidth: width,
        minWidth: leafColumnMinWidths[index] ?? width,
      },
    ];
  });
};

export const augmentWithMeasurements = (
  viewModel: ViewpointModeComponentMatrixViewModel
): ViewpointModeComponentMatrixViewModel => {
  const { measurements, rowModels } = measure(viewModel);
  const columnsByDepthWithGridInfo = viewModel.columnsByDepthWithGridInfo.map(
    level =>
      level.map(column => {
        if (
          column.ownerType !==
            ComponentMatrixGroupOwnerType.REFERENCED_COMPONENT ||
          column.otherLabels.length === 0
        ) {
          return column;
        }
        return {
          ...column,
          otherLabelWrappedLines:
            computeColumnHeaderOtherLabelWrappedLines(column),
        };
      })
  );
  return {
    ...viewModel,
    columnsByDepthWithGridInfo,
    rowLevelHeader: toLevelHeaderViewModel(viewModel.rowLevelLabels),
    columnLevelHeader: toLevelHeaderViewModel(viewModel.columnLevelLabels),
    measurements,
    rowModels,
    userAdjustedColumnWidths: viewModel.userAdjustedColumnWidths ?? {},
    resizeHandles: toResizeHandles(measurements),
  };
};

export const withCarriedBypassLimit = (
  previousState: ComponentMatrix2BuiltState,
  args: ComponentMatrix2ResetViewModelArgs
): ComponentMatrix2ViewModelArgs => ({
  ...args,
  // Carry the bypass across redraws of the same dataset, but re-prompt on a
  // real context switch. viewSourceData$ re-emits on formatting/label/sort
  // changes with the same loadedGraph (only a new traversal changes it), so a
  // stable loadedGraph reference means "same dataset".
  bypassLimit:
    args.loadedGraph === previousState.loadedGraph
      ? previousState.bypassLimit
      : false,
});

export const getBypassLimitArgs = (
  state: ComponentMatrix2BuiltState,
  onProceedAnyway: VoidFunction
): ComponentMatrix2ViewModelArgs => ({
  viewSettings: state.viewSettings,
  loadedGraph: state.loadedGraph,
  bypassLimit: true,
  onProceedAnyway,
  i18nLocale: state.i18nLocale,
  orgUsersById: state.orgUsersById,
  sort: state.sort,
  userLocale: state.userLocale,
  contextComponentId: state.contextComponentId,
  permissionContext: state.permissionContext,
  formattingAndLabelRules: state.formattingAndLabelRules,
});

/** Settings whose change alone doesn't trigger a reset. The view saves them from the shown view
 *  model, and the next reset reads them. */
export const IGNORE_VIEW_SETTINGS: (keyof ComponentMatrix2ViewSettings)[] = [
  'persistedExpandedState',
];

// Cap at 1000 per axis to prevent view settings from bloating unboundedly
// (block diagram caps at 2000 total; each CM2 entry is an address array, not a single ID).
const MAX_EXPANDED_ADDRESSES = 1000;

type ExpandableGroups = Pick<
  ViewpointModeComponentMatrixViewModel,
  'rows' | 'columns'
>;

const collectExpandedState = (
  viewModel: ExpandableGroups
): PersistedExpandedState => ({
  expandedRowAddresses: collectExpandedAddresses(viewModel.rows).slice(
    0,
    MAX_EXPANDED_ADDRESSES
  ),
  expandedColumnAddresses: collectExpandedAddresses(viewModel.columns).slice(
    0,
    MAX_EXPANDED_ADDRESSES
  ),
});

/** The expanded state to save when the rendered groups changed, or null when their expansion
 *  didn't. Only rendered groups are saved, so groups that are filtered out or past the node limit
 *  lose their expansion. */
export const getExpandedStateToPersist = (
  prevViewModel: ExpandableGroups,
  viewModel: ExpandableGroups
): PersistedExpandedState | null => {
  if (
    prevViewModel.rows === viewModel.rows &&
    prevViewModel.columns === viewModel.columns
  )
    return null;
  const expandedState = collectExpandedState(viewModel);
  return isEqual(collectExpandedState(prevViewModel), expandedState)
    ? null
    : expandedState;
};
